import { useContext, useCallback, useEffect, useRef, useState } from "react";
import { BsSearch } from "react-icons/bs";
import { HiX } from "react-icons/hi";
import { RxHamburgerMenu } from "react-icons/rx";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FaChevronDown, FaChevronUp, FaStoreAlt } from "react-icons/fa";
import { Modal, button } from "react-bootstrap";
import { base_url } from "../utils/axiosConfig";
import { AuthContext } from "../context";
import {
  FaUserTie,
  FaUsers,
  FaUser,
  FaLock,
  FaClock,
  FaHandshake,
  FaBuilding,
  FaBriefcase,
  FaStore,
  FaChartLine,
  FaCheckCircle,
} from "react-icons/fa";

import "bootstrap/dist/css/bootstrap.min.css";
import RoleInfoModal from "./roleModal";

const Header = () => {
  const { isAuthenticated, setIsAuthenticated } = useContext(AuthContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState({
    categories: [],
    subcategories: [],
    subSubcategories: [],
    subSubSubcategories: [],
    products: [],
  });
  const [showResults, setShowResults] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const timeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currency = localStorage.getItem("currency");

  const token = localStorage.getItem("user_website")
    ? JSON.parse(localStorage.getItem("user_website"))
    : JSON.parse(localStorage.getItem("user"))
      ? JSON.parse(localStorage.getItem("user"))
      : null;

  const isWalletUser = token?.userType === "member";

  const debounce = (func, delay) => {
    return (...args) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => func(...args), delay);
    };
  };

  const fetchProducts = useCallback(async (term = "") => {
    try {
      const response = await fetch(
        `${base_url}product/getCategory?page=1&limit=100&sortingColumn=createdAt&sortingOrder=asc&search=${encodeURIComponent(
          term,
        )}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      if (term) {
        let categories = [];
        let subcategories = [];
        let subSubcategories = [];
        let subSubSubcategories = [];
        let products = [];

        if (Array.isArray(data?.data) && data.data[0]?.categoryName) {
          categories = data.data;
          subcategories = categories.flatMap((cat) =>
            (cat.subCategories || []).map((sub) => ({
              ...sub,
              parentCategory: cat,
            })),
          );
          subSubcategories = subcategories.flatMap((sub) =>
            (sub.subSubCategories || []).map((subSub) => ({
              ...subSub,
              parentCategory: sub.parentCategory,
              parentSubCategory: sub,
            })),
          );
          subSubSubcategories = subSubcategories.flatMap((subSub) =>
            (subSub.subSubSubCategories || []).map((subSubSub) => ({
              ...subSubSub,
              parentCategory: subSub.parentCategory,
              parentSubCategory: subSub.parentSubCategory,
              parentSubSubCategory: subSub,
            })),
          );
          products = [
            ...categories.flatMap((cat) => cat.products || []),
            ...subcategories.flatMap((sub) => sub.products || []),
            ...subSubcategories.flatMap((subSub) => subSub.products || []),
            ...subSubSubcategories.flatMap(
              (subSubSub) => subSubSub.products || [],
            ),
          ].filter(
            (product, index, self) =>
              product && self.findIndex((p) => p._id === product._id) === index,
          );
        } else if (Array.isArray(data?.data) && data.data[0]?.subCategories) {
          subcategories = data.data.flatMap((item) =>
            (item.subCategories || []).map((sub) => ({
              ...sub,
              parentCategory: item.parentCategory || {
                categoryName: "Unknown Category",
              },
            })),
          );
          subSubcategories = subcategories.flatMap((sub) =>
            (sub.subSubCategories || []).map((subSub) => ({
              ...subSub,
              parentCategory: sub.parentCategory,
              parentSubCategory: sub,
            })),
          );
          subSubSubcategories = subSubcategories.flatMap((subSub) =>
            (subSub.subSubSubCategories || []).map((subSubSub) => ({
              ...subSubSub,
              parentCategory: subSub.parentCategory,
              parentSubCategory: subSub.parentSubCategory,
              parentSubSubCategory: subSub,
            })),
          );
          products = [
            ...subcategories.flatMap((sub) => sub.products || []),
            ...subSubcategories.flatMap((subSub) => subSub.products || []),
            ...subSubSubcategories.flatMap(
              (subSubSub) => subSubSub.products || [],
            ),
          ].filter(
            (product, index, self) =>
              product && self.findIndex((p) => p._id === product._id) === index,
          );
        } else if (
          Array.isArray(data?.data) &&
          data.data[0]?.subSubCategories
        ) {
          subSubcategories = data.data.flatMap((item) =>
            (item.subSubCategories || []).map((subSub) => ({
              ...subSub,
              parentCategory: item.parentCategory || {
                categoryName: "Unknown Category",
              },
              parentSubCategory: item.parentSubCategory || {
                subCategoryName: "Unknown Subcategory",
              },
            })),
          );
          subSubSubcategories = subSubcategories.flatMap((subSub) =>
            (subSub.subSubSubCategories || []).map((subSubSub) => ({
              ...subSubSub,
              parentCategory: subSub.parentCategory,
              parentSubCategory: subSub.parentSubCategory,
              parentSubSubCategory: subSub,
            })),
          );
          products = [
            ...subSubcategories.flatMap((subSub) => subSub.products || []),
            ...subSubSubcategories.flatMap(
              (subSubSub) => subSubSub.products || [],
            ),
          ].filter(
            (product, index, self) =>
              product && self.findIndex((p) => p._id === product._id) === index,
          );
        } else if (
          Array.isArray(data?.data) &&
          data.data[0]?.subSubSubCategories
        ) {
          subSubSubcategories = data.data.flatMap((item) =>
            (item.subSubSubCategories || []).map((subSubSub) => ({
              ...subSubSub,
              parentCategory: item.parentCategory || {
                categoryName: "Unknown Category",
              },
              parentSubCategory: item.parentSubCategory || {
                subCategoryName: "Unknown Subcategory",
              },
              parentSubSubCategory: item.parentSubSubCategory || {
                subSubCategoryName: "Unknown Sub-subcategory",
              },
            })),
          );
          products = subSubSubcategories
            .flatMap((subSubSub) => subSubSub.products || [])
            .filter(
              (product, index, self) =>
                product &&
                self.findIndex((p) => p._id === product._id) === index,
            );
        } else if (Array.isArray(data?.data) && data.data[0]?.productName) {
          products = data.data.map((product) => ({
            ...product,
            parentCategory: product.parentCategory || {
              categoryName: "Unknown Category",
            },
            parentSubCategory: product.parentSubCategory || null,
            parentSubSubCategory: product.parentSubSubCategory || null,
            parentSubSubSubCategory: product.parentSubSubSubCategory || null,
          }));
        }

        const lowerTerm = term.trim().toLowerCase();
        setSearchResults({
          categories: categories.filter(
            (cat) =>
              cat.categoryName &&
              cat.categoryName.trim().toLowerCase().includes(lowerTerm),
          ),
          subcategories: subcategories.filter(
            (sub) =>
              sub.subCategoryName &&
              sub.subCategoryName.trim().toLowerCase().includes(lowerTerm),
          ),
          subSubcategories: subSubcategories.filter(
            (subSub) =>
              subSub.subSubCategoryName &&
              subSub.subSubCategoryName
                .trim()
                .toLowerCase()
                .includes(lowerTerm),
          ),
          subSubSubcategories: subSubSubcategories.filter(
            (subSubSub) =>
              subSubSub.subSubSubCategoryName &&
              subSubSub.subSubSubCategoryName
                .trim()
                .toLowerCase()
                .includes(lowerTerm),
          ),
          products: products.filter(
            (prod) =>
              prod.productName &&
              prod.productName.trim().toLowerCase().includes(lowerTerm),
          ),
        });
        setShowResults(true);
      } else {
        setCategories(data?.data || []);
        setSearchResults({
          categories: [],
          subcategories: [],
          subSubcategories: [],
          subSubSubcategories: [],
          products: [],
        });
        setShowResults(false);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to fetch search results. Please try again.");
    }
  }, []);

  const debouncedFetchProducts = useCallback(debounce(fetchProducts, 500), [
    fetchProducts,
  ]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (isFilterSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isFilterSidebarOpen]);

  useEffect(() => {
    if (location.state?.openChannelPartnerSignUp) {
      const timer = setTimeout(() => {
        navigate("/signup/channelpartner", { state: {}, replace: true });
      }, 500);
      return () => clearTimeout(timer);
    }

    if (location.state?.openMemberSignUp) {
      const timer = setTimeout(() => {
        navigate("/signup/member", { state: {}, replace: true });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.state, navigate]);

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    debouncedFetchProducts(term);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      setShowResults(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowResults(false);
      }
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !event.target.closest(".filter-button")
      ) {
        setIsFilterSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsFilterSidebarOpen(false);
  };

  const toggleFilterSidebar = () => {
    setIsFilterSidebarOpen(!isFilterSidebarOpen);
    setIsMenuOpen(false);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleSubcategorySelect = (category, subcategory) => {
    navigate(`/category/${category?._id}`, {
      state: {
        category,
        subcategory,
        products: subcategory.products,
      },
    });
    setIsFilterSidebarOpen(false);
  };

  const handleAccountClick = () => {
    setShowAccountModal(true);
  };

  const handleTypeClick = (type, action) => {
    setSelectedType(type);

    const basePath = action === "login" ? "/signin" : "/signup";
    if (type === "enduser") {
      // Direct navigation for End User
      navigate(`${basePath}/${type}`);
      if (action === "login") {
        setShowAccountModal(false);
      }
    }
    if (action === "login") {
      if (type === "channelpartner") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/partnerlogin",
          "_blank",
        );
      } else if (type === "channelpartnerplus") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/partnerpluslogin",
          "_blank",
        );
      } else if (type === "corporate") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/corporatelogin",
          "_blank",
        );
      } else if (type === "director") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/directorlogin",
          "_blank",
        );
      } else if (type === "franchise") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/franchiselogin",
          "_blank",
        );
      } else if (type === "vendor") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/vendorlogin",
          "_blank",
        );
      } else if (type === "member") {
        navigate(`${basePath}/${type}`);
      }
    }

    if (action === "register") {
      navigate(`${basePath}/${type}`);
    }
  };

  const proceedToAction = () => {
    navigate(`signup/${selectedType}`);
    setShowDetailsModal(false);
  };
  console.log(isWalletUser);
  const handleLogout = () => {
    localStorage.removeItem("user_website");
    if (isWalletUser) localStorage.removeItem("user");
    sessionStorage.clear();
    setIsAuthenticated(false);
    navigate("/", { state: { from: location.pathname } });
    setIsFilterSidebarOpen(false);
    window.location.reload();
  };

  const formatForUrl = (name) => {
    if (!name) {
      console.warn("formatForUrl received undefined or empty name");
      return "unnamed";
    }
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const findCategoryHierarchyForProduct = (productId) => {
    for (const category of categories || []) {
      if (!category.categoryName) {
        console.warn(`Category with ID ${category._id} has no categoryName`);
        continue;
      }
      for (const subCategory of category.subCategories || []) {
        if ((subCategory.products || []).some((p) => p._id === productId)) {
          return {
            category,
            subCategory,
            subSubCategory: null,
            subSubSubCategory: null,
          };
        }
        for (const subSubCategory of subCategory.subSubCategories || []) {
          if (
            (subSubCategory.products || []).some((p) => p._id === productId)
          ) {
            return {
              category,
              subCategory,
              subSubCategory,
              subSubSubCategory: null,
            };
          }
          for (const subSubSubCategory of subSubCategory.subSubSubCategories ||
            []) {
            if (
              (subSubSubCategory.products || []).some(
                (p) => p._id === productId,
              )
            ) {
              return {
                category,
                subCategory,
                subSubCategory,
                subSubSubCategory,
              };
            }
          }
        }
      }
    }
    console.warn(`No hierarchy found for product ID ${productId}`);
    return {
      category: null,
      subCategory: null,
      subSubCategory: null,
      subSubSubCategory: null,
    };
  };

  const findCategoryHierarchyForSubSubCategory = (subSubCategoryId) => {
    for (const category of categories || []) {
      if (!category.categoryName) {
        console.warn(`Category with ID ${category._id} has no categoryName`);
        continue;
      }
      for (const subCategory of category.subCategories || []) {
        for (const subSubCategory of subCategory.subSubCategories || []) {
          if (subSubCategory._id === subSubCategoryId) {
            return {
              category,
              subCategory,
              subSubCategory,
              subSubSubCategory: null,
            };
          }
          for (const subSubSubCategory of subSubCategory.subSubSubCategories ||
            []) {
            if (subSubSubCategory._id === subSubCategoryId) {
              return {
                category,
                subCategory,
                subSubCategory,
                subSubSubCategory,
              };
            }
          }
        }
      }
    }
    console.warn(
      `No hierarchy found for subSubCategory ID ${subSubCategoryId}`,
    );
    return {
      category: null,
      subCategory: null,
      subSubCategory: null,
      subSubSubCategory: null,
    };
  };

  const findCategoryHierarchyForSubSubSubCategory = (subSubSubCategoryId) => {
    for (const category of categories || []) {
      if (!category.categoryName) {
        console.warn(`Category with ID ${category._id} has no categoryName`);
        continue;
      }
      for (const subCategory of category.subCategories || []) {
        for (const subSubCategory of subCategory.subSubCategories || []) {
          for (const subSubSubCategory of subSubCategory.subSubSubCategories ||
            []) {
            if (subSubSubCategory._id === subSubSubCategoryId) {
              return {
                category,
                subCategory,
                subSubCategory,
                subSubSubCategory,
              };
            }
          }
        }
      }
    }
    console.warn(
      `No hierarchy found for subSubSubCategory ID ${subSubSubCategoryId}`,
    );
    return {
      category: null,
      subCategory: null,
      subSubCategory: null,
      subSubSubCategory: null,
    };
  };

  return (
    <>
      <header className="bg-white shadow-sm z-20 relative">
        <div className="px-4 md:px-4 flex flex-col md:flex-row justify-between items-center gap-4 py-2">
          <div className="flex items-center justify-between w-full md:w-auto">
            <div className="flex items-center gap-2 md:gap-3">
              <img
                className="cursor-pointer h-10 md:h-12"
                src="/lifeon.svg"
                alt="logo"
                height="40"
                onClick={() =>
                  navigate("/", { state: { from: location.pathname } })
                }
              />
            </div>
            <button
              className="md:hidden text-gray-600 focus:outline-none"
              onClick={toggleMenu}
            >
              <RxHamburgerMenu size={28} />
            </button>
          </div>

          <div
            className="w-full md:w-1/2 lg:w-1/3 relative"
            ref={searchInputRef}
          >
            <form onSubmit={handleSubmit}>
              <BsSearch
                className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-500 cursor-pointer"
                size={16}
                onClick={handleSubmit}
              />
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-full focus:outline-none"
                placeholder="Search for categories, subcategories, or products"
                value={searchTerm}
                onChange={handleInputChange}
                onFocus={() => searchTerm && setShowResults(true)}
              />
            </form>

            {showResults && (
              <div className="absolute top-full left-0 w-full max-w-[calc(100vw-1rem)] bg-white border border-gray-200 rounded-lg shadow-lg mt-2 z-50 max-h-80 overflow-y-auto">
                {searchResults.categories?.length === 0 &&
                  searchResults.subcategories?.length === 0 &&
                  searchResults.subSubcategories?.length === 0 &&
                  searchResults.subSubSubcategories?.length === 0 &&
                  searchResults.products?.length === 0 && (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      {searchTerm
                        ? "No results found"
                        : "Start typing to search"}
                    </div>
                  )}

                {searchResults.categories?.length > 0 && (
                  <div className="border-b border-gray-200">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500">
                      Categories
                    </div>
                    {searchResults.categories.map((category) => (
                      <div
                        key={category._id}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-3"
                        onClick={() => {
                          const categoryName =
                            category.categoryName || "Unnamed Category";
                          navigate(`/category/${formatForUrl(categoryName)}`, {
                            state: {
                              category,
                              subCategory: null,
                              subSubCategory: null,
                              subSubSubCategory: null,
                            },
                          });
                          setShowResults(false);
                          setSearchTerm("");
                        }}
                      >
                        {category.images?.[0] && (
                          <img
                            src={category.images[0]}
                            alt={category.categoryName || "Category"}
                            className="w-10 h-10 object-cover rounded"
                            onError={(e) => (e.target.src = "/placeholder.png")}
                          />
                        )}
                        <div>
                          <p className="text-sm font-semibold">
                            {category.categoryName || "Unnamed Category"}
                          </p>
                          <p className="text-xs text-gray-500 line-clamp-1">
                            {category.description || "No description available"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {searchResults.subcategories?.length > 0 && (
                  <div className="border-b border-gray-200">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500">
                      Subcategories
                    </div>
                    {searchResults.subcategories.map((subcategory) => {
                      const parentCategoryName =
                        subcategory.parentCategory?.categoryName ||
                        "Unnamed Category";
                      return (
                        <div
                          key={subcategory._id}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-3"
                          onClick={() => {
                            if (
                              !parentCategoryName ||
                              !subcategory.subCategoryName
                            ) {
                              console.warn(
                                `Skipping navigation for subcategory ${subcategory._id}: missing fields`,
                                { subcategory },
                              );
                              toast.error(
                                "Unable to navigate: Missing category or subcategory name",
                              );
                              return;
                            }
                            const path = `/category/${formatForUrl(
                              parentCategoryName,
                            )}/${formatForUrl(subcategory.subCategoryName)}`;
                            navigate(path, {
                              state: {
                                category: subcategory.parentCategory,
                                subCategory: subcategory,
                                subSubCategory: null,
                                subSubSubCategory: null,
                              },
                            });
                            setShowResults(false);
                            setSearchTerm("");
                          }}
                        >
                          {subcategory.images?.[0] && (
                            <img
                              src={subcategory.images[0]}
                              alt={subcategory.subCategoryName || "Subcategory"}
                              className="w-10 h-10 object-cover rounded"
                              onError={(e) =>
                                (e.target.src = "/placeholder.png")
                              }
                            />
                          )}
                          <div>
                            <p className="text-sm font-semibold">
                              {subcategory.subCategoryName ||
                                "Unnamed Subcategory"}
                            </p>
                            <p className="text-xs text-gray-500 line-clamp-1">
                              {subcategory.description ||
                                "No description available"}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {searchResults.subSubcategories?.length > 0 && (
                  <div className="border-b border-gray-200">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500">
                      Sub-subcategories
                    </div>
                    {searchResults.subSubcategories.map((subSubcategory) => {
                      const hierarchy = findCategoryHierarchyForSubSubCategory(
                        subSubcategory._id,
                      );
                      const parentCategoryName =
                        hierarchy.category?.categoryName ||
                        subSubcategory.parentCategory?.categoryName ||
                        "Unnamed Category";
                      const parentSubCategoryName =
                        hierarchy.subCategory?.subCategoryName ||
                        subSubcategory.parentSubCategory?.subCategoryName ||
                        "Unnamed Subcategory";
                      return (
                        <div
                          key={subSubcategory._id}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-3"
                          onClick={() => {
                            if (
                              !parentCategoryName ||
                              !parentSubCategoryName ||
                              !subSubcategory.subSubCategoryName
                            ) {
                              console.warn(
                                `Skipping navigation for subSubcategory ${subSubcategory._id}: missing fields`,
                                { subSubcategory },
                              );
                              toast.error(
                                "Unable to navigate: Missing category or subcategory names",
                              );
                              return;
                            }
                            const path = `/category/${formatForUrl(
                              parentCategoryName,
                            )}/${formatForUrl(
                              parentSubCategoryName,
                            )}/${formatForUrl(
                              subSubcategory.subSubCategoryName,
                            )}`;
                            navigate(path, {
                              state: {
                                category:
                                  hierarchy.category ||
                                  subSubcategory.parentCategory,
                                subCategory:
                                  hierarchy.subCategory ||
                                  subSubcategory.parentSubCategory,
                                subSubCategory:
                                  hierarchy.subSubCategory || subSubcategory,
                                subSubSubCategory: null,
                              },
                            });
                            setShowResults(false);
                            setSearchTerm("");
                          }}
                        >
                          {subSubcategory.images?.[0] && (
                            <img
                              src={subSubcategory.images[0]}
                              alt={
                                subSubcategory.subSubCategoryName ||
                                "Sub-subcategory"
                              }
                              className="w-10 h-10 object-cover rounded"
                              onError={(e) =>
                                (e.target.src = "/placeholder.png")
                              }
                            />
                          )}
                          <div>
                            <p className="text-sm font-semibold">
                              {subSubcategory.subSubCategoryName ||
                                "Unnamed Sub-subcategory"}
                            </p>
                            <p className="text-xs text-gray-500 line-clamp-1">
                              {subSubcategory.description ||
                                "No description available"}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {searchResults.subSubSubcategories?.length > 0 && (
                  <div className="border-b border-gray-200">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500">
                      Sub-sub-subcategories
                    </div>
                    {searchResults.subSubSubcategories.map(
                      (subSubSubcategory) => {
                        const hierarchy =
                          findCategoryHierarchyForSubSubSubCategory(
                            subSubSubcategory._id,
                          );
                        const parentCategoryName =
                          hierarchy.category?.categoryName ||
                          subSubSubcategory.parentCategory?.categoryName ||
                          "Unnamed Category";
                        const parentSubCategoryName =
                          hierarchy.subCategory?.subCategoryName ||
                          subSubSubcategory.parentSubCategory
                            ?.subCategoryName ||
                          "Unnamed Subcategory";
                        const parentSubSubCategoryName =
                          hierarchy.subSubCategory?.subSubCategoryName ||
                          subSubSubcategory.parentSubSubCategory
                            ?.subSubCategoryName ||
                          "Unnamed Sub-subcategory";
                        return (
                          <div
                            key={subSubSubcategory._id}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-3"
                            onClick={() => {
                              if (
                                !parentCategoryName ||
                                !parentSubCategoryName ||
                                !parentSubSubCategoryName ||
                                !subSubSubcategory.subSubSubCategoryName
                              ) {
                                console.warn(
                                  `Skipping navigation for subSubSubcategory ${subSubSubcategory._id}: missing fields`,
                                  { subSubSubcategory },
                                );
                                toast.error(
                                  "Unable to navigate: Missing category or subcategory names",
                                );
                                return;
                              }
                              const path = `/category/${formatForUrl(
                                parentCategoryName,
                              )}/${formatForUrl(
                                parentSubCategoryName,
                              )}/${formatForUrl(
                                parentSubSubCategoryName,
                              )}/${formatForUrl(
                                subSubSubcategory.subSubSubCategoryName,
                              )}`;
                              navigate(path, {
                                state: {
                                  category:
                                    hierarchy.category ||
                                    subSubSubcategory.parentCategory,
                                  subCategory:
                                    hierarchy.subCategory ||
                                    subSubSubcategory.parentSubCategory,
                                  subSubCategory:
                                    hierarchy.subSubCategory ||
                                    subSubSubcategory.parentSubSubCategory,
                                  subSubSubCategory:
                                    hierarchy.subSubSubCategory ||
                                    subSubSubcategory,
                                },
                              });
                              setShowResults(false);
                              setSearchTerm("");
                            }}
                          >
                            {subSubSubcategory.images?.[0] && (
                              <img
                                src={subSubSubcategory.images[0]}
                                alt={
                                  subSubSubcategory.subSubSubCategoryName ||
                                  "Sub-sub-subcategory"
                                }
                                className="w-10 h-10 object-cover rounded"
                                onError={(e) =>
                                  (e.target.src = "/placeholder.png")
                                }
                              />
                            )}
                            <div>
                              <p className="text-sm font-semibold">
                                {subSubSubcategory.subSubSubCategoryName ||
                                  "Unnamed Sub-sub-subcategory"}
                              </p>
                              <p className="text-xs text-gray-500 line-clamp-1">
                                {subSubSubcategory.description ||
                                  "No description available"}
                              </p>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                )}

                {searchResults.products?.length > 0 && (
                  <div>
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500">
                      Products
                    </div>
                    {searchResults.products.map((product) => {
                      const hierarchy = findCategoryHierarchyForProduct(
                        product._id,
                      );
                      const parentCategoryName =
                        hierarchy.category?.categoryName ||
                        product.parentCategory?.categoryName ||
                        "Unnamed Category";
                      return (
                        <div
                          key={product._id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-3"
                          onClick={() => {
                            const path = `/product-details/${product._id}`;
                            navigate(path, {
                              state: {
                                product,
                                category:
                                  hierarchy.category || product.parentCategory,
                                subCategory:
                                  hierarchy.subCategory ||
                                  product.parentSubCategory,
                                subSubCategory:
                                  hierarchy.subSubCategory ||
                                  product.parentSubSubCategory,
                                subSubSubCategory:
                                  hierarchy.subSubSubCategory ||
                                  product.parentSubSubSubCategory,
                              },
                            });
                            setShowResults(false);
                            setSearchTerm("");
                          }}
                        >
                          {product.variations?.[0]?.images?.[0] ||
                            (product?.images?.[0] && (
                              <img
                                src={
                                  product?.variations?.[0]?.images[0] ||
                                  product?.images?.[0]
                                }
                                alt={product?.productName || "Product"}
                                className="w-10 h-10 object-cover rounded"
                                onError={(e) =>
                                  (e.target.src = "/placeholder.png")
                                }
                              />
                            ))}
                          <div>
                            <p className="text-sm font-semibold">
                              {product.productName || "Unnamed Product"}
                            </p>
                            <p className="text-xs text-gray-500 line-clamp-1">
                              {product.briefDescription ||
                                "No description available"}
                            </p>
                            {product.variations?.[0]?.price?.amount && (
                              <p className="text-xs text-gray-600">
                                ₹{product.variations[0].price.amount}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="hidden md:flex items-center gap-3 lg:gap-4">
            {/* Navigation links with active highlighting */}
            <nav className="flex items-center gap-3 text-sm font-medium text-gray-700">
              {[
                { path: "/", label: "Home" },
                { path: "/services", label: "Services" },
                { path: "/about", label: "About Us" },
                { label: "News & Events", path: "/news-events" },
                { label: "Gallery", path: "/gallery" },
                { label: "Press Media", path: "/press-media" },
                { path: "/contact", label: "Contact Us" },
                { path: "/blog", label: "Blogs" },
              ].map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`py-2 rounded transition-all ${
                    location.pathname === item.path
                      ? "bg-green-100 text-[#35A93E] underline underline-offset-4 font-medium"
                      : "hover:text-[#35A93E] hover:bg-green-50/60"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            {isAuthenticated ? (
              <>
                <a href="/cart" className="relative">
                  <span className="text-2xl text-gray-600 hover:text-[#35A93E]">
                    🛒
                  </span>
                  Cart
                </a>
                <a href="/order-history" className="relative">
                  <span className="text-2xl text-gray-600 hover:text-[#35A93E]">
                    📋
                  </span>
                  Order
                </a>
                {!isWalletUser && (
                  <a href="/profile" className="relative">
                    <span className="text-2xl text-gray-600 hover:text-[#35A93E]">
                      👤
                    </span>
                    Profile
                  </a>
                )}
                <button
                  className="text-white text-xs md:text-sm bg-[#35A93E] hover:bg-[#2e8b36] no-underline border-none cursor-pointer px-4 py-[7px] rounded"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </>
            ) : (
              <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
                <button
                  className="flex flex-row justify-center items-center w-[120px] text-white sm:w-[140px] md:w-[171px] h-[40px] sm:h-[45px] md:h-[49px] bg-[#02BE0F] rounded-[2px] px-[8px] sm:px-[10px] gap-[8px] sm:gap-[10px]"
                  onClick={handleAccountClick}
                >
                  Account
                </button>
              </div>
            )}
          </div>
        </div>

        {/* {location.pathname === "/" && currency && (
          <nav className="hidden w-full bg-[#061939] py-3 px-4 md:px-6 md:flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 lg:gap-10 text-white font-['Poppins'] font-medium text-xs leading-[18px] sm:leading-[21px] z-10">
            <div
              className="flex flex-row items-center gap-1 cursor-pointer hover:opacity-80 filter-button"
              onClick={toggleFilterSidebar}
            >
              <span>Filter</span>
              <FaChevronDown size={12} />
            </div>

            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6 lg:gap-8 xl:gap-12 text-center">
              {categories?.slice(0, 6)?.map((category) => (
                <div
                  key={category?._id}
                  className="flex flex-row items-center gap-0.5 cursor-pointer"
                  onClick={() => {
                    const categoryName =
                      category.categoryName || "Unnamed Category";
                    navigate(`/category/${formatForUrl(categoryName)}`, {
                      state: {
                        category,
                        subCategory: null,
                        subSubCategory: null,
                        subSubSubCategory: null,
                      },
                    });
                  }}
                >
                  <span>{category?.categoryName || "Unnamed Category"}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-row items-center gap-0.5 cursor-pointer hover:opacity-80">
              <span>Support</span>
            </div>
          </nav>
        )} */}
      </header>

      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
          isFilterSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={toggleFilterSidebar}
      ></div>

      <div
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-full w-80 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${
          isFilterSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between py-3 px-4 bg-[#061939] text-white">
          <h2 className="text-lg font-semibold flex-1">Filters</h2>
          <button
            className="text-white focus:outline-none"
            onClick={toggleFilterSidebar}
          >
            <HiX size={24} />
          </button>
        </div>
        <div className="p-4 overflow-y-auto h-[calc(100%-4rem)]">
          {categories?.map((category) => (
            <div key={category._id} className="mb-4">
              <div
                className="flex items-center justify-between cursor-pointer py-2"
                onClick={() => toggleCategory(category._id)}
              >
                <span className="text-sm font-medium text-gray-800">
                  {category.categoryName || "Unnamed Category"}
                </span>
                {expandedCategories[category._id] ? (
                  <FaChevronUp className="text-gray-600" size={12} />
                ) : (
                  <FaChevronDown className="text-gray-600" size={12} />
                )}
              </div>
              {expandedCategories[category._id] && (
                <div className="ml-4">
                  {category.subCategories.map((subcategory) => (
                    <div
                      key={subcategory._id}
                      className="py-2 text-sm text-gray-600 hover:text-[#35A93E] cursor-pointer"
                      onClick={() =>
                        handleSubcategorySelect(category, subcategory)
                      }
                    >
                      {subcategory.subCategoryName || "Unnamed Subcategory"}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div
        className={`fixed top-0 right-0 h-full w-64 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        } md:hidden`}
      >
        <div className="flex justify-end p-4">
          <button
            onClick={toggleMenu}
            className="text-gray-600 focus:outline-none"
          >
            <HiX size={24} />
          </button>
        </div>
        <div className="flex flex-col p-4 space-y-5">
          <div className="flex flex-col space-y-4 border-b pb-5">
            {[
              { path: "/channel-partner", label: "Channel Partner" },
              { path: "/partner-model", label: "Partner Model" },
              { path: "/contact", label: "Contact" },
              // { path: "/faq", label: "FAQ" },
              // { path: "/privacy-policy", label: "Privacy Policy" },
            ].map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setIsMenuOpen(false);
                }}
                className={`text-left py-2 px-3 rounded transition-all ${
                  location.pathname === item.path
                    ? "bg-green-100 text-[#35A93E] underline underline-offset-4 font-medium"
                    : "text-gray-800 hover:text-[#35A93E] hover:bg-green-50/40"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          {isAuthenticated ? (
            <div className="flex flex-col space-y-4">
              <a
                href="/cart"
                className="flex items-center gap-2 text-gray-800 text-sm hover:text-[#35A93E] no-underline"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="text-xl">🛒</span>
                Cart
              </a>
              <a
                href="/order-history"
                className="flex items-center gap-2 text-gray-800 text-sm hover:text-[#35A93E] no-underline"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="text-xl">📋</span>
                Orders
              </a>
              <button
                className="text-gray-800 text-sm hover:text-[#35A93E] no-underline text-left"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          ) : (
            <div className="flex flex-col space-y-4">
              <button
                onClick={handleAccountClick}
                className="flex flex-row justify-center items-center w-[120px] text-white sm:w-[140px] md:w-[171px] h-[40px] sm:h-[45px] md:h-[49px] bg-[#02BE0F] rounded-[2px] px-[8px] sm:px-[10px] gap-[8px] sm:gap-[10px]"
              >
                Account
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Account Selection Modal */}
      <Modal
        show={showAccountModal}
        onHide={() => setShowAccountModal(false)}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Choose Your Account Type</Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 py-4">
          <div className="row g-4">
            {/* ACTIVE ACCOUNTS */}
            {[
              {
                title: "Member",
                icon: <FaUsers />,
                desc: "Shop products, track orders and enjoy member benefits.",
                type: "member",
                bg: "#e9f9ee",
                color: "#02BE0F",
                knowMore: true,
              },
              {
                title: "Channel Partner",
                icon: <FaHandshake />,
                desc: "For distributors and sales partners to grow business with us.",
                type: "channelpartner",
                bg: "#e7f1ff",
                color: "#0d6efd",
                knowMore: true,
              },
              {
                title: "Franchise",
                icon: <FaStoreAlt />,
                desc: "For distributors and sales partners to grow business with us.",
                type: "franchise",
                bg: "#e7f1ff",
                color: "#0d6efd",
                // knowMore: true,
              },
              {
                title: "Channel Partner Plus",
                icon: <FaUserTie />,
                desc: "Premium partners with higher commission and priority support.",
                type: "channelpartnerplus",
                bg: "#fff4e5",
                color: "#ff9800",
              },
              {
                title: "Corporates & Industries & Professionals",
                icon: <FaBuilding />,
                desc: "For companies and professionals buying in bulk or services.",
                type: "corporate",
                bg: "#f3e8ff",
                color: "#6f42c1",
                knowMore: true,
              },
              {
                title: "Director / Super Distributor",
                icon: <FaBriefcase />,
                desc: "Manage distribution network and oversee partner operations.",
                type: "director",
                bg: "#f1f3f5",
                color: "#343a40",
              },
              {
                title: "Vendor",
                icon: <FaStore />,
                desc: "Sell your products and manage inventory through our platform.",
                type: "vendor",
                bg: "#e6fffa",
                color: "#20c997",
                knowMore: true,
              },
              {
                title: "End User",
                icon: <FaUser />,
                desc: "Purchase and use products/services directly from platform.",
                type: "enduser",
                bg: "#f8f9fa",
                color: "#6c757d",
                knowMore: true,
              },
            ].map((item) => (
              <div className="col-md-4" key={item.title}>
                <div
                  className="border rounded-4 p-4 h-100 d-flex flex-column shadow-sm"
                  style={{
                    transition: "0.3s",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-6px)";
                    e.currentTarget.style.boxShadow =
                      "0 10px 20px rgba(0,0,0,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  <h5 className="mb-2 d-flex align-items-center gap-2">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "45px",
                        height: "45px",
                        background: item.bg,
                        color: item.color,
                        fontSize: "20px",
                      }}
                    >
                      {item.icon}
                    </div>
                    {item.title}
                  </h5>

                  <p className="text-muted small flex-grow-1 mb-3">
                    {item.desc}
                  </p>

                  {item.knowMore && (
                    <p
                      className="text-primary small text-decoration-underline mb-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        if (item.type === "channelpartner") {
                          setShowDetailsModal(true);
                        } else {
                          setShowModal(true);
                          setSelectedType(item.type);
                        }
                      }}
                    >
                      Click here to know more
                    </p>
                  )}

                  <div className="d-flex gap-2 mt-auto">
                    <button
                      className="flex justify-center items-center w-[100px] h-[35px]  bg-[#02BE0F] text-white rounded  hover:bg-[#029e0c] transition duration-200"
                      onClick={() => handleTypeClick(item.type, "register")}
                    >
                      Register
                    </button>

                    <button
                      className="flex justify-center items-center w-[100px] h-[35px] border border-[#02BE0F] text-[#02BE0F] bg-white rounded"
                      onClick={() => handleTypeClick(item.type, "login")}
                    >
                      Login
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* COMING SOON */}
            {[
              { label: "Affiliate", icon: <FaHandshake /> },
              { label: "Investor", icon: <FaChartLine /> },
              // { label: "Franchise", icon: <FaStoreAlt /> },
            ].map((item) => (
              <div className="col-md-4" key={item.label}>
                <div className="border rounded-4 p-4 h-100 d-flex flex-column shadow-sm opacity-75">
                  <h5 className="mb-2 d-flex align-items-center gap-2">
                    <FaLock className="text-secondary" size={18} />
                    <span style={{ fontSize: "24px" }}>{item.icon}</span>
                    {item.label}

                    <span className="badge bg-warning text-dark ms-auto d-flex align-items-center">
                      <FaClock className="me-1" size={12} />
                      Coming Soon
                    </span>
                  </h5>

                  <p className="text-muted small flex-grow-1 mb-4">
                    This account type will be available soon. Stay tuned!
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            variant="secondary"
            onClick={() => setShowAccountModal(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* Details Modal - Text Content Only */}
      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        centered
        size="xl"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-success">
            {selectedType === "channelpartner"
              ? "Channel Partner"
              : "Channel Partner Plus"}{" "}
            Opportunity
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 px-md-5 py-4 bg-light">
          {/* ================= HERO ================= */}
          <div className="mb-5 py-5 bg-dark text-white rounded-3">
            <div className="container">
              <div className="text-center w-full">
                <h3 className="fw-bold mb-3" style={{ fontSize: "32px" }}>
                  Now You Enjoy A{" "}
                  <span className="text-warning">
                    Unique Business Opportunity
                  </span>
                </h3>
              </div>
              <div className="row align-items-center">
                {/* RIGHT IMAGE */}
                <div className="col-md-6 text-center d-flex justify-center mt-4 mt-md-0">
                  <img
                    src="/partnerSignup/0.png" // 👈 your 2-person image path
                    alt="Business Partners"
                    style={{ height: "250px" }}
                  />
                </div>

                {/* LEFT CONTENT */}
                <div className="col-md-6 text-md-start text-center">
                  <p className="mb-2 fs-5">With New Marketing Technology</p>

                  <p className="fw-semibold mb-3">
                    <span className="text-warning">
                      User | Partner | Vendor | E-Commerce
                    </span>
                    <br />
                    <span className="text-white-50">
                      (Multi-connect Software – Instant Payment Gateway)
                    </span>
                  </p>

                  <h4 className="fw-bold text-warning mt-4">
                    First Time <span className="text-white">In the World</span>
                  </h4>

                  <h5 className="fw-bold mt-2">
                    With{" "}
                    <span className="text-warning">5 Types of Incentives</span>
                    In The{" "}
                    <span className="text-warning">Traditional Business</span>
                  </h5>
                </div>
              </div>
              <div className="text-center w-full">
                <h4 className="fw-bold mt-4 text-success text-3xl">
                  1 Ecosystem – 10+ Platforms
                </h4>

                <p className="text-white-50 mt-2">
                  World’s First – Most Advanced Medical & Digital Healthcare
                  Technological Products & Services
                </p>
              </div>
            </div>
          </div>

          {/* ================= INTRODUCTION ================= */}
          <div className="mb-5">
            <div className="w-full text-center">
              {" "}
              <h4
                className="fw-bold text-success mb-4 underline"
                style={{
                  fontSize: "32px",
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                }}
              >
                Introduction
              </h4>
            </div>
            <div className="row align-items-center">
              {/* RIGHT IMAGE */}
              <div className="col-md-6 text-center d-flex justify-center mt-4 mt-md-0">
                <img
                  src="/partnerSignup/0.1.png" // 👈 add your image path
                  alt="Lifeonplus Digital Healthcare"
                  className="img-fluid rounded"
                  style={{ maxHeight: "250px" }}
                />
              </div>
              {/* LEFT CONTENT */}
              <div className="col-md-6">
                <p className="mb-4">
                  Welcome to <strong>Lifeonplus</strong> – a revolutionary
                  platform for digital healthcare solutions. Join us in
                  empowering healthcare worldwide while unlocking lucrative
                  business opportunities.
                </p>

                <p>
                  Lifeonplus is the world’s most advanced digital healthcare
                  platform, uniquely offering comprehensive solutions across all
                  healthcare verticals. We revolutionize care by making
                  high-tech, quality healthcare affordable and accessible. With
                  decades of expertise, our creative and innovative approach
                  transforms ideas into impactful realities— empowering
                  patients, partners, vendors, and professionals alike.
                </p>
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= WHY CHOOSE US (3 BOXES) ================= */}
          <div className="mb-5">
            <h4
              className="fw-bold text-success mb-5 text-center underline"
              style={{
                fontSize: "32px",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Why Choose Us?
            </h4>

            <div className="row g-4">
              <div className="col-md-4">
                <div className="p-4 bg-white border rounded shadow-sm h-100">
                  <h5 className="fw-bold text-success mb-2">
                    Advanced & Accessible
                  </h5>
                  <p className="small mb-0">
                    Smart digital healthcare solutions that bridge healthcare
                    gaps using advanced technology while remaining affordable
                    and accessible.
                  </p>
                </div>
              </div>

              <div className="col-md-4">
                <div className="p-4 bg-white border rounded shadow-sm h-100">
                  <h5 className="fw-bold text-success mb-2">Global Impact</h5>
                  <p className="small mb-0">
                    A scalable global ecosystem serving individuals, corporates,
                    institutions, and communities across regions and countries.
                  </p>
                </div>
              </div>

              <div className="col-md-4">
                <div className="p-4 bg-white border rounded shadow-sm h-100">
                  <h5 className="fw-bold text-success mb-2">
                    Transformative Growth
                  </h5>
                  <p className="small mb-0">
                    Lucrative business opportunities designed to create
                    sustainable income, long-term growth, and financial
                    stability.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= WHY LIFEONPLUS ================= */}
          <div className="mb-5">
            <h4
              className="fw-bold text-success mb-5 text-center underline"
              style={{
                fontSize: "32px",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Why Lifeonplus?
            </h4>

            <div className="row align-items-center">
              {/* LEFT ICON LIST */}
              <div
                className="col-md-3 d-flex flex-column align-items-center"
                style={{ gap: "24px" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "16px 18px",
                    borderRadius: "14px",
                    background: "#f8f9fa",
                    fontSize: "15px",
                    fontWeight: "600",
                    width: "100%",
                    maxWidth: "280px",
                  }}
                >
                  <img
                    src="/partnerSignup/2.png"
                    alt="Medical"
                    style={{
                      width: "52px",
                      height: "52px",
                      objectFit: "contain",
                    }}
                  />
                  <span>Medical & Digital Health</span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "16px 18px",
                    borderRadius: "14px",
                    background: "#f8f9fa",
                    fontSize: "15px",
                    fontWeight: "600",
                    width: "100%",
                    maxWidth: "280px",
                  }}
                >
                  <img
                    src="/partnerSignup/1.png"
                    alt="AI"
                    style={{
                      width: "52px",
                      height: "52px",
                      objectFit: "contain",
                    }}
                  />
                  <span>Artificial Intelligence</span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "16px 18px",
                    borderRadius: "14px",
                    background: "#f8f9fa",
                    fontSize: "15px",
                    fontWeight: "600",
                    width: "100%",
                    maxWidth: "280px",
                  }}
                >
                  <img
                    src="/partnerSignup/3.png"
                    alt="Enterprise"
                    style={{
                      width: "52px",
                      height: "52px",
                      objectFit: "contain",
                    }}
                  />
                  <span>Enterprise Solutions</span>
                </div>
              </div>

              {/* CENTER DESCRIPTION */}
              <div className="col-md-6">
                <p
                  style={{
                    fontSize: "16px",
                    lineHeight: "1.9",
                  }}
                >
                  Lifeonplus is built on a strong foundation of innovation,
                  technology, and enterprise-grade healthcare solutions. It is
                  not just a healthcare platform, but a complete digital
                  ecosystem designed to scale globally and deliver real-world
                  impact.
                </p>

                <p
                  style={{
                    fontSize: "14px",
                    color: "#6c757d",
                    marginTop: "18px",
                  }}
                >
                  Innovation Beyond Imagination – transforming healthcare
                  delivery and business opportunities for a smarter future.
                </p>
              </div>

              {/* RIGHT ICON LIST */}
              <div
                className="col-md-3 d-flex flex-column align-items-center"
                style={{ gap: "24px" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "16px 18px",
                    borderRadius: "14px",
                    background: "#f8f9fa",
                    fontSize: "15px",
                    fontWeight: "600",
                    width: "100%",
                    maxWidth: "280px",
                  }}
                >
                  <img
                    src="/partnerSignup/4.png"
                    alt="Blockchain"
                    style={{
                      width: "52px",
                      height: "52px",
                      objectFit: "contain",
                    }}
                  />
                  <span>Blockchain Technology</span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "16px 18px",
                    borderRadius: "14px",
                    background: "#f8f9fa",
                    fontSize: "15px",
                    fontWeight: "600",
                    width: "100%",
                    maxWidth: "280px",
                  }}
                >
                  <img
                    src="/partnerSignup/5.png"
                    alt="IoT"
                    style={{
                      width: "52px",
                      height: "52px",
                      objectFit: "contain",
                    }}
                  />
                  <span>Internet of Things (IoT)</span>
                </div>
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= SECURE HEALTHCARE ================= */}
          <div className="mb-5 p-4 bg-white border rounded shadow-sm">
            <h4
              className="fw-bold text-success mb-4 underline"
              style={{
                fontSize: "30px",
                lineHeight: "1.3",
              }}
            >
              Secure, Efficient Healthcare with Smart Cards, Blockchain QR
              Technology & AI
            </h4>

            <div className="row align-items-center">
              {/* LEFT CONTENT */}
              <div className="col-md-6">
                <p style={{ fontSize: "15px", lineHeight: "1.7" }}>
                  Our patented QR code system allows instant verification of
                  health documents, ensuring authenticity and quick access to
                  vital information.
                </p>

                <p style={{ fontSize: "15px", lineHeight: "1.7" }}>
                  Blockchain technology ensures secure, tamper-proof storage and
                  transfer of health records. It serves as a unified, reliable
                  platform that minimizes fraud and strengthens patient trust.
                </p>

                <ul
                  className="list-unstyled"
                  style={{ fontSize: "14px", marginTop: "12px" }}
                >
                  <li>• Immutable health records</li>
                  <li>• Enhanced data security</li>
                  <li>• Transparency in transactions</li>
                  <li>• HIPAA compliant</li>
                  <li>• Body screening technologies</li>
                </ul>

                {/* RFID + NFC */}
                <div
                  style={{
                    display: "flex",
                    gap: "20px",
                    alignItems: "center",
                    lineHeight: "1.7",
                  }}
                  className="mt-5"
                >
                  <img
                    src="/partnerSignup/1.2.png"
                    alt="RFID"
                    style={{ width: "55px" }}
                  />
                  <img
                    src="/partnerSignup/1.5.png"
                    alt="NFC"
                    style={{ width: "55px" }}
                  />
                </div>

                {/* LABEL */}
                <div
                  style={{
                    fontWeight: "700",
                    fontSize: "17px",
                    marginTop: "4px",
                  }}
                >
                  Patented QR Technology
                </div>
              </div>

              {/* RIGHT VISUAL BLOCK */}
              <div className="col-md-6">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "18px",
                  }}
                >
                  {/* TOP IMAGES */}
                  <div
                    style={{
                      display: "flex",
                      gap: "20px",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src="/partnerSignup/1.3.png"
                      alt="Health Card"
                      style={{ width: "110px" }}
                    />

                    <img
                      src="/partnerSignup/1.1.png"
                      alt="Smart Card"
                      style={{
                        width: "130px",
                        borderRadius: "14px",
                      }}
                    />
                  </div>

                  {/* MAIN IMAGE */}
                  <img
                    src="/partnerSignup/1.4.png"
                    alt="Health Records"
                    style={{
                      width: "260px",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= BUSINESS PROJECTION ================= */}
          <div className="mb-5">
            <h4
              className="fw-bold text-success mb-5 text-center underline"
              style={{
                fontSize: "32px",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Healthcare Business Projection
            </h4>

            <p className="mb-4 text-xl">
              The global digital health market is projected to grow at a CAGR of
              18.6% over the next five years. driven by increased demand for
              personalized healthcare and remote monitoring solutions
            </p>

            <h3 className="font-bold">Key Statistics:</h3>

            <ul>
              <li>1: Market size (2023): $200 billion</li>
              <li>2: Projected size (2028): $500 billion</li>
              <li>
                3: Adoption rate of Wearable health tech: 65% among millennials
              </li>
            </ul>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= HEALTHCARE PORTFOLIO ================= */}
          <div className="mb-5">
            <h4
              className="fw-bold text-success mb-4 text-center underline"
              style={{
                fontSize: "34px",
                letterSpacing: "0.5px",
              }}
            >
              Our Comprehensive Healthcare Portfolio
            </h4>

            <p
              className="text-center mb-5"
              style={{ fontSize: "15px", maxWidth: "750px", margin: "0 auto" }}
            >
              Lifeonplus addresses a wide range of healthcare needs through
              innovative digital solutions.
            </p>

            <div className="row align-items-start">
              {/* LEFT ICONS (5) */}
              <div className="col-md-2 d-flex flex-column align-items-center gap-4 mt-3">
                <img
                  src="/partnerSignup/2.1.png"
                  alt=""
                  style={{ width: "150px" }}
                />
                <img
                  src="/partnerSignup/2.2.png"
                  alt=""
                  style={{ width: "150px" }}
                />
                <img
                  src="/partnerSignup/2.3.png"
                  alt=""
                  style={{ width: "150px" }}
                />
                <img
                  src="/partnerSignup/2.4.png"
                  alt=""
                  style={{ width: "150px" }}
                />
              </div>

              {/* CENTER CONTENT (5 ITEMS) */}
              <div className="col-md-7">
                <div className="mb-2 p-4 bg-white border rounded shadow-sm">
                  <h6 className="fw-bold text-success">
                    Digital Healthcare Technologies
                  </h6>
                  <p className="small mb-0">
                    Smart health cards, remote monitoring, and quick health
                    checkups for faster and easier access to care.
                  </p>
                </div>

                <div className="mb-2 p-4 bg-white border rounded shadow-sm">
                  <h6 className="fw-bold text-success">
                    Preventive Healthcare & Diagnostics
                  </h6>
                  <p className="small mb-0">
                    Advanced body screening, express medical tests, and
                    non-invasive diagnostic solutions to help prevent diseases.
                  </p>
                </div>

                <div className="mb-2 p-4 bg-white border rounded shadow-sm">
                  <h6 className="fw-bold text-success">
                    Medical Records & IT Solutions
                  </h6>
                  <p className="small mb-0">
                    Secure and accessible electronic medical records (EMR/EHR)
                    and personal health records (PHR) to ensure that vital
                    information is always at hand.
                  </p>
                </div>

                <div className="mb-2 p-4 bg-white border rounded shadow-sm">
                  <h6 className="fw-bold text-success">
                    Innovative Medical Devices
                  </h6>
                  <p className="small mb-0">
                    Wearable technologies, skin carotenoid tests, kiosks, and
                    scalar energy products that support effective patient care.
                  </p>
                </div>

                {/* ✅ MISSING ITEM – NOW ADDED */}
                <div className="p-4 bg-white border rounded shadow-sm">
                  <h6 className="fw-bold text-success">
                    Food Supplements & Wellness
                  </h6>
                  <p className="small mb-0">
                    A range of supplements and digital health studios to support
                    overall well-being and preventive care.
                  </p>
                </div>
              </div>

              {/* RIGHT VISUALS (3) */}
              <div
                className="col-md-3 d-flex flex-column align-items-center gap-4"
                style={{ paddingTop: "10px" }}
              >
                <img
                  src="/partnerSignup/2.5.png"
                  alt=""
                  style={{ width: "150px" }}
                />

                <img
                  src="/partnerSignup/2.6.png"
                  alt=""
                  style={{ width: "170px" }}
                />

                <img
                  src="/partnerSignup/2.7.png"
                  alt=""
                  style={{ width: "160px" }}
                />
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* =================  Lifeonplus Digital Healthcare ================= */}
          <div className="py-10 px-3">
            {/* Main Heading */}
            <h4
              className="fw-bold text-success mb-4 text-center underline"
              style={{
                fontSize: "34px",
                letterSpacing: "0.5px",
              }}
            >
              Lifeonplus Digital Healthcare
            </h4>

            {/* 🔹 MAIN 3 COLUMNS */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* ================= PRESERVATIONS & PRESENTATION ================= */}
              <div className="col-md-2">
                <div className="bg-green-600 text-white py-3 text-center font-bold rounded-xl mb-4">
                  Preservations & Presentation
                </div>

                <div className="bg-[rgb(171,253,200)] text-[#166534] py-2 text-center font-semibold rounded-md mb-2">
                  Medical Records
                </div>

                {["PHR", "EMR", "EHR"].map((item) => (
                  <div
                    key={item}
                    className="bg-[#f0fdf4] text-[#14532d] py-2 text-center rounded-md mb-1 text-sm border border-[#374151]"
                  >
                    {item}
                  </div>
                ))}
              </div>

              {/* ================= PREVENTIVE HEALTHCARE ================= */}
              <div className="col-md-6">
                <div className="bg-green-600 text-white py-3 text-center font-bold rounded-xl mb-4">
                  Preventive Healthcare <br /> (Body Screening)
                </div>

                {/* 🔹 3 SUB COLUMNS */}
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Invasive */}
                  <div className="flex-1">
                    <div className="bg-[rgb(171,253,200)] text-[#166534] py-2 text-center font-semibold rounded-md mb-2">
                      Invasive
                    </div>
                    {[
                      "Diagnosis",
                      "Clinics",
                      "Labs",
                      "Hospitals",
                      "Doctors",
                    ].map((item) => (
                      <div
                        key={item}
                        className="bg-[#f0fdf4] text-[#14532d] py-2 text-center rounded-md mb-1 text-sm border border-[#374151]"
                      >
                        {item}
                      </div>
                    ))}
                  </div>

                  {/* Non-Invasive */}
                  <div className="flex-1">
                    <div className="bg-[rgb(171,253,200)] text-[#166534] py-2 text-center font-semibold rounded-md mb-2">
                      Non-Invasive
                    </div>
                    {[
                      "Digital Health Studio",
                      "Quick Health Checkup",
                      "Xpress Medical Test",
                      "Body Recharge Station",
                      "Wearable Technology",
                      "Skin Carotenoid",
                    ].map((item) => (
                      <div
                        key={item}
                        className="bg-[#f0fdf4] text-[#14532d] py-2 text-center rounded-md mb-1 text-sm border border-[#374151]"
                      >
                        {item}
                      </div>
                    ))}
                  </div>

                  {/* Contactless */}
                  <div className="flex-1">
                    <div className="bg-[rgb(171,253,200)] text-[#166534] py-2 text-center font-semibold rounded-md mb-2">
                      Contactless
                    </div>
                    {["AI", "All Vitals"].map((item) => (
                      <div
                        key={item}
                        className="bg-[#f0fdf4] text-[#14532d] py-2 text-center rounded-md mb-1 text-sm border border-[#374151]"
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ================= SOLUTIONS BY NEW TECHNOLOGIES ================= */}
              <div className="col-md-4">
                <div className="bg-green-600 text-white py-3 text-center font-bold rounded-xl mb-4 ">
                  Solutions by <br /> New Technologies
                </div>

                {/* 🔹 2 SUB COLUMNS */}
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="bg-[rgb(171,253,200)] text-[#166534] py-2 text-center font-semibold rounded-md mb-2">
                      Non-Invasive Solutions
                    </div>
                    {["Scalar Wave", "Nano Technology", "MEOL"].map((item) => (
                      <div
                        key={item}
                        className="bg-[#f0fdf4] text-[#14532d] py-2 text-center rounded-md mb-1 text-sm border border-[#374151]"
                      >
                        {item}
                      </div>
                    ))}
                  </div>

                  <div className="flex-1">
                    <div className="bg-[rgb(171,253,200)] text-[#166534] py-2 text-center font-semibold rounded-md mb-2">
                      Food <br />
                      Supplements
                    </div>
                    {["FDA / CE Certified", "Launching Soon…"].map((item) => (
                      <div
                        key={item}
                        className="bg-[#f0fdf4] text-[#14532d] py-2 text-center rounded-md mb-1 text-sm border border-[#374151]"
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= PRODUCTS & SERVICES ================= */}
          <div className="mb-5">
            <h4
              className="fw-bold text-success mb-4 text-center underline"
              style={{
                fontSize: "34px",
                letterSpacing: "0.5px",
              }}
            >
              Products & Services
            </h4>

            <div className="row g-4">
              {[
                {
                  title: "Digital Health Smart Card",
                  img: "/partnerSignup/3.1.png",
                  desc: "Access your complete medical history anytime with our secure digital health smart card, integrating emergency info and critical records for instant doctor review.",
                },
                {
                  title: "Diagnosis",
                  img: "/partnerSignup/3.2.png",
                  desc: "Benefit from our innovative diagnostic tools that combine technology and expert analysis to provide accurate health assessments for better outcomes.",
                },
                {
                  title: "Quick Health Checkup",
                  img: "/partnerSignup/3.3.png",
                  desc: "Get fast and reliable health checkups using streamlined non-invasive diagnostic solutions that enable early detection and timely intervention.",
                },
                {
                  title: "Express Medical Test",
                  img: "/partnerSignup/3.4.png",
                  desc: "Experience rapid express medical tests that deliver precise results in minutes, ensuring prompt diagnosis and efficient treatment planning.",
                },
                {
                  title: "Remote Patient Monitoring",
                  img: "/partnerSignup/3.5.png",
                  desc: "Remote Patient Monitoring (RPM) enables real-time tracking of vital signs and chronic conditions via wearables and AI, reducing in-person visits.",
                },
                {
                  title: "Body Recharge Station",
                  img: "/partnerSignup/3.6.png",
                  desc: "Experience a state-of-the-art body recharge station that revitalizes your energy and promotes overall wellness through non-invasive advanced technology.",
                },
                {
                  title: "Scalar Energy",
                  img: "/partnerSignup/3.7.png",
                  desc: "Harness the power of scalar energy – a non-invasive, drug-free technology that boosts cell regeneration, metabolism, and blood circulation.",
                },
                {
                  title: "Kiosk",
                  img: "/partnerSignup/3.8.png",
                  desc: "Our portable kiosks offer quick health checks and automated diagnostics, providing on-the-spot access to vital health information for communities.",
                },
                {
                  title: "Wearable Technology",
                  img: "/partnerSignup/3.9.png",
                  desc: "Stay connected to your health 24/7 with our cutting-edge wearable devices that continuously track vital signs and fitness metrics.",
                },
                {
                  title: "Skin Carotenoid Test",
                  img: "/partnerSignup/3.10.png",
                  desc: "Measure your antioxidant levels in seconds with our non-invasive skin carotenoid test—an indicator of overall health and immune status.",
                },
                {
                  title: "AI – 6·32 Vitals",
                  img: "/partnerSignup/3.11.png",
                  desc: "Leverage our advanced AI system to monitor 6–32 vital parameters in real time, delivering precise insights for proactive healthcare.",
                },
                {
                  title: "Innovative Medical Devices",
                  img: "/partnerSignup/3.12.png",
                  desc: "Discover our range of innovative medical devices that blend advanced technology with user-friendly design for accurate healthcare solutions.",
                },
                {
                  title: "Food Supplements",
                  img: "/partnerSignup/3.13.png",
                  desc: "Enhance your health with tailored food supplement solutions designed to support preventive care and boost immunity naturally every day.",
                },
                {
                  title: "Digital Health Studio",
                  img: "/partnerSignup/3.14.png",
                  desc: "Explore our digital health studio—an integrated platform for managing health data, personalized diagnostics, and interactive wellness programs.",
                },
              ].map((item, index) => (
                <div className="col-md-4" key={index}>
                  <div
                    className={`h-100 rounded shadow-sm p-4 text-center bg-white border`}
                  >
                    <div className="d-flex justify-content-center mb-3">
                      <img
                        src={item.img}
                        alt={item.title}
                        style={{ height: 100 }}
                      />
                    </div>

                    <h6 className={`fw-bold mb-2 text-success`}>
                      {item.title}
                    </h6>

                    <p className="small mb-0">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= BUSINESS MODEL ================= */}
          <div
            className="mb-5 p-4 rounded"
            style={{ backgroundColor: "#F4FBF6" }}
          >
            {/* Heading */}
            <h4
              className="fw-bold text-success mb-4 text-center underline"
              style={{ fontSize: "34px", letterSpacing: "0.5px" }}
            >
              Business Model
            </h4>

            <div className="row g-4 align-items-stretch justify-content-center">
              {/* ================= LEFT COLUMN ================= */}
              <div className="col-md-5">
                <div className="row g-4 h-100">
                  {/* CHANNEL PARTNER */}
                  <div className="col-12">
                    <div className="text-center d-flex flex-column h-100">
                      <div className="bg-success text-white rounded-pill py-2 fw-bold mb-2">
                        Channel Partner
                      </div>

                      <div className="bg-white rounded p-4 shadow-sm flex-grow-1 d-flex flex-column justify-content-center">
                        <p className="mb-1 fw-semibold">Country</p>
                        <p className="mb-1 fw-semibold">State</p>
                        <p className="mb-1 fw-semibold">District</p>
                        <p className="mb-1 fw-semibold">Pin Code</p>
                        <p className="mb-0 fw-semibold">Retailers</p>
                      </div>
                    </div>
                  </div>

                  {/* KEY ACCOUNTS */}
                  <div className="col-12">
                    <div className="position-relative text-center d-flex flex-column h-100">
                      <div className="bg-success text-white rounded-pill py-2 fw-bold mb-2">
                        Key Accounts
                      </div>

                      <div className="bg-white border rounded p-4 shadow-sm flex-grow-1 d-flex flex-column justify-content-center">
                        <p className="mb-1 fw-semibold">Institutions</p>
                        <p className="mb-1 fw-semibold">Industries</p>
                        <p className="mb-1 fw-semibold">Corporates</p>
                        <p className="mb-1 fw-semibold">
                          Associations & Others
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ================= CENTER COLUMN ================= */}
              <div className="col-md-2 d-flex flex-column align-items-center justify-content-between">
                <img
                  src="/partnerSignup/4.1.png"
                  alt="Business Model"
                  className="img-fluid"
                  style={{ height: 130 }}
                />
                <img
                  src="/partnerSignup/4.2.png"
                  alt="Healthcare Network"
                  className="img-fluid my-4"
                  style={{ height: 130 }}
                />
                <img
                  src="/partnerSignup/4.3.png"
                  alt="Healthcare Network"
                  className="img-fluid"
                  style={{ height: 130 }}
                />
              </div>

              {/* ================= RIGHT COLUMN ================= */}
              <div className="col-md-5">
                <div className="row g-4 h-100">
                  {/* MEDICAL FRATERNITY */}
                  <div className="col-12">
                    <div className="position-relative text-center d-flex flex-column h-100">
                      <div className="bg-success text-white rounded-pill py-2 fw-bold mb-2">
                        Medical Fraternity
                      </div>

                      <div className="bg-white border rounded p-4 shadow-sm flex-grow-1 d-flex flex-column justify-content-center">
                        <p className="mb-1 fw-semibold">Hospitals</p>
                        <p className="mb-1 fw-semibold">Labs</p>
                        <p className="mb-1 fw-semibold">Clinics</p>
                        <p className="mb-1 fw-semibold">Medical Shops</p>
                        <p className="mb-0 fw-semibold">Doctors</p>
                      </div>
                    </div>
                  </div>

                  {/* SALES CHANNELS */}
                  <div className="col-12">
                    <div className="position-relative text-center d-flex flex-column h-100">
                      <div className="bg-success text-white rounded-pill py-2 fw-bold mb-2">
                        Sales Channels
                      </div>

                      <div className="bg-white border rounded p-4 shadow-sm flex-grow-1 d-flex flex-column justify-content-center">
                        <p
                          className="mb-1 fw-semibold"
                          style={{ opacity: 0.55 }}
                        >
                          Direct Sales (Coming Soon)
                        </p>
                        <p className="mb-1 fw-semibold">Referral Sales</p>
                        <p className="mb-1 fw-semibold">Online Sales</p>
                        <p className="mb-1 fw-semibold">Virtual Partnership</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= REVENUE GENERATION STREAMS ================= */}
          <div
            className="mb-5 p-4 rounded"
            style={{ backgroundColor: "#F4FBF6" }}
          >
            <h4
              className="fw-bold text-success mb-4 text-center underline"
              style={{ fontSize: "34px", letterSpacing: "0.5px" }}
            >
              Revenue Generation Streams
            </h4>

            <div className="row align-items-center g-4">
              {/* LEFT CONTENT */}
              <div className="col-md-6">
                <p className="mb-3 text-xl">
                  <strong className="text-success">Lifeonplus</strong> operates
                  on a multi-channel business model that integrates direct
                  sales, referral marketing, online sales, and virtual
                  partnerships.
                </p>

                <p className="text-[#00a84b] mb-4 text-lg">
                  These diverse streams not only ensure robust profitability but
                  also create significant opportunities for our partners.
                </p>
              </div>

              {/* RIGHT IMAGE */}
              <div className="col-md-6 text-center">
                <img
                  src="/partnerSignup/5.1.png"
                  alt="Revenue Streams"
                  className="img-fluid"
                  style={{ height: 400 }}
                />
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= UNIQUE MARKETING MODEL ================= */}
          <div
            className="mb-5 p-4 rounded position-relative"
            style={{ backgroundColor: "#F4FBF6" }}
          >
            <h4
              className="fw-bold text-success mb-2 text-center underline"
              style={{ fontSize: "34px", letterSpacing: "0.5px" }}
            >
              Unique Marketing Model
            </h4>

            <p className="text-center fw-semibold text-success mb-5">
              How It Works – Automated Income
            </p>

            <div className="row align-items-center">
              {/* ================= LEFT ICON STACK ================= */}
              <div className="col-md-4 text-center">
                <div className="d-flex justify-content-center gap-4 mb-4 flex-wrap">
                  <img
                    src="/partnerSignup/6.2.png"
                    alt="Online Purchase"
                    style={{ height: 80 }}
                  />
                  <img
                    src="/partnerSignup/6.3.png"
                    alt="Location Tracking"
                    style={{ height: 80 }}
                  />
                  <img
                    src="/partnerSignup/6.4.png"
                    alt="Automated System"
                    style={{ height: 80 }}
                  />
                </div>

                {/* Bottom-left icon */}
                <div className="d-flex justify-content-center mt-4">
                  <img
                    src="/partnerSignup/6.1.png"
                    alt="Earnings"
                    style={{ height: 200 }}
                  />
                </div>
              </div>

              {/* ================= CENTER CONTENT ================= */}
              <div className="col-md-4 text-center text-lg">
                <p className="mb-3">
                  Imagine a customer makes an <strong>online purchase</strong>,
                  corporate sale, or referral — our automated system captures
                  its location and instantly allocates rewards.
                </p>

                <p className="fw-semibold text-success mb-3">
                  Pin Code / District / State & Countries
                </p>

                <p className="text-muted mb-0">
                  Earn a <strong>passive income bonus</strong> even when you’re
                  not actively working and without using your own stock.
                </p>
              </div>

              {/* ================= RIGHT ICON STACK ================= */}
              <div className="col-md-4 text-center">
                <div className="d-flex justify-content-center gap-4 mb-4 flex-wrap">
                  <img
                    src="/partnerSignup/6.5.png"
                    alt="Growth Analytics"
                    style={{ height: 150 }}
                  />
                </div>

                {/* Bottom-right icon */}
                <div className="d-flex justify-content-center mt-4">
                  <img
                    src="/partnerSignup/6.7.png"
                    alt="Passive Income"
                    style={{ height: 200 }}
                  />
                  <img
                    src="/partnerSignup/6.6.png"
                    alt="Network Expansion"
                    style={{ height: 250 }}
                  />
                </div>
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= UNLOCK SUCCESS ================= */}
          <div
            className="mb-5 p-4 rounded text-center"
            style={{ backgroundColor: "#F4FBF6" }}
          >
            <h4
              className="fw-bold mb-2 text-center underline"
              style={{ fontSize: "34px", letterSpacing: "0.5px" }}
            >
              Unlock Success with{" "}
              <span className="text-success">Lifeonplus</span>
            </h4>

            <p className="text-muted mb-4">
              Partner with us and achieve a <strong>180-day break-even</strong>,
              or enjoy our <strong>12-month money-back guarantee</strong>.
            </p>

            <div className="row align-items-center g-4">
              {/* ================= LEFT – 90 DAYS BREAK EVEN ================= */}
              <div className="col-md-6">
                <div className="bg-white rounded shadow-sm p-4 h-100 text-center">
                  {/* 🔹 IMAGES IN SAME ROW */}
                  <div className="d-flex justify-content-center gap-3 mb-3">
                    <img
                      src="/partnerSignup/7.1.png"
                      alt="90 Days Break Even"
                      style={{ height: 70 }}
                    />
                    <img
                      src="/partnerSignup/7.2.png"
                      alt="90 Days Break Even"
                      style={{ height: 70 }}
                    />
                  </div>

                  <h5 className="fw-bold">180 Days</h5>
                  <p className="text-success fw-semibold mb-1">Break-even*</p>
                  <p className="text-muted small mb-0">
                    Achieve financial stability within 180 days from the date of
                    activation.
                  </p>
                </div>
              </div>

              {/* ================= RIGHT – MONEY BACK GUARANTEE ================= */}
              <div className="col-md-6">
                <div className="bg-white rounded shadow-sm p-4 h-100 text-center">
                  {/* 🔹 CENTERED IMAGE */}
                  <div className="d-flex justify-content-center mb-3">
                    <img
                      src="/partnerSignup/7.3.png"
                      alt="Money Back Guarantee"
                      style={{ height: 70 }}
                    />
                  </div>

                  <h5 className="fw-bold">Money Back</h5>
                  <p className="text-success fw-semibold mb-1">Guarantee</p>
                  <p className="text-muted small mb-0">
                    Enjoy a risk-free partnership with our 12-month money-back
                    guarantee. (Conditions Apply)
                  </p>
                </div>
              </div>
            </div>

            {/* FOOTNOTE */}
            <p className="text-muted small mt-3 mb-0">
              *From the date of activation
            </p>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= OUR PARTNERSHIP MODEL ================= */}
          <div className="mb-5">
            <h4
              className="fw-bold text-success mb-2 text-center underline"
              style={{ fontSize: "34px", letterSpacing: "0.5px" }}
            >
              Our Partnership Model
            </h4>

            <p
              className="fw-semibold text-success mb-4 text-center"
              style={{ fontSize: "20px" }}
            >
              We offer opportunities for everyone
            </p>
            <div className="row align-items-center g-5">
              {/* ================= LEFT CONTENT ================= */}
              <div className="col-md-6">
                <p className="text-muted mb-4">
                  At Lifeonplus, we offer three distinct pathways for success:
                </p>

                <ul className="list-unstyled mb-4">
                  <li className="mb-3">
                    <strong className="text-primary">Channel Partner:</strong>{" "}
                    Take an active role by investing in regional and national
                    opportunities with tailored support and high ROI.
                  </li>
                  <li className="mb-3">
                    <strong className="text-success">
                      Referral Marketing:
                    </strong>{" "}
                    Earn commissions by connecting people with Lifeonplus
                    healthcare solutions and services.
                  </li>

                  <li className="mb-3">
                    <strong className="text-warning">Virtual Partner:</strong>{" "}
                    Enjoy returns with passive involvement through digital and
                    virtual investment opportunities.
                  </li>
                </ul>

                <p className="fw-semibold mb-0">
                  Choose the model that best fits your ambition and join us in
                  transforming healthcare while building lasting wealth.
                </p>

                <div
                  className="mt-3"
                  style={{
                    width: "220px",
                    height: "2px",
                    backgroundColor: "#000",
                  }}
                />
              </div>

              {/* ================= RIGHT IMAGE ================= */}
              <div className="col-md-6 text-center">
                <img
                  src="/partnerSignup/8.1.png"
                  alt="Partnership Model"
                  className="img-fluid"
                  style={{ maxHeight: "420px" }}
                />
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= CORE PILLARS OF PRODUCT SUCCESS ================= */}
          <div className="p-4 rounded mt-5" style={{ background: "#f6fbf8" }}>
            <h4
              className="text-center mb-4 fw-bold underline"
              style={{ fontSize: "34px", letterSpacing: "0.5px" }}
            >
              Core Pillars of{" "}
              <span className="text-success">Product Success</span>
            </h4>

            <div className="row align-items-center g-4">
              {/* ================= LEFT CONTENT ================= */}
              <div className="col-md-5">
                <div className="mb-4 d-flex gap-2 align-items-start">
                  <FaCheckCircle className="text-success mt-1" size={20} />
                  <div>
                    <strong className="text-xl">Unique Offering</strong>
                    <div className="text-muted small text-lg">
                      No direct competitors
                    </div>
                  </div>
                </div>

                <div className="mb-4 d-flex gap-2 align-items-start">
                  <FaCheckCircle className="text-success mt-1" size={20} />
                  <div>
                    <strong className="text-xl">Prospective Clients</strong>
                    <div className="text-muted small text-lg">
                      Every living human being
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-2 align-items-start">
                  <FaCheckCircle className="text-success mt-1" size={20} />
                  <div>
                    <strong className="text-xl">Growth Potential</strong>
                    <div className="text-muted small text-lg">
                      100% scalability
                    </div>
                  </div>
                </div>
              </div>

              {/* ================= RIGHT IMAGE ================= */}
              <div className="col-md-7 text-center">
                <img
                  src="/partnerSignup/8.2.png"
                  alt="Core Pillars of Product Success"
                  className="img-fluid"
                  style={{ maxHeight: "420px" }}
                />
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= INCOME STRUCTURE ================= */}
          <div className="container my-5">
            {/* Heading */}
            <div className="text-center mb-4">
              <h2
                className="fw-bold text-success underline"
                style={{ fontSize: "34px", letterSpacing: "0.5px" }}
              >
                Income Structure
              </h2>
            </div>

            <div
              className="row rounded-4 p-4"
              style={{ backgroundColor: "#f6faf7" }}
            >
              {/* Income Points */}
              <div className="col-md-7">
                {/* Direct Income */}
                <div className="mb-4">
                  <h5 className="fw-bold text-dark mb-2">
                    <span className="text-success text-lg">●</span> Direct
                    Income
                  </h5>
                  <p className="mb-0">
                    <span className="text-success fw-bold">✔</span> Earn a fixed
                    commission on every sale — your success starts with every
                    transaction.
                  </p>
                </div>

                {/* Referral Commission */}
                <div className="mb-4">
                  <h5 className="fw-bold text-dark mb-2">
                    <span className="text-primary text-lg">●</span> Referral
                    Commissions
                  </h5>
                  <p className="mb-1">
                    <span className="text-success fw-bold">✔</span> Earn up to{" "}
                    <strong>15%</strong> commission for every new partner you
                    introduce.
                  </p>
                  <p className="mb-0">
                    <span className="text-success fw-bold">✔</span> Get rewarded
                    for growing the network and building a strong community.
                  </p>
                </div>

                {/* Reward Income */}
                <div>
                  <h5 className="fw-bold text-dark mb-2 text-lg">
                    🏆 Reward Income
                  </h5>
                  <p className="mb-1">
                    <span className="text-success fw-bold">✔</span> Unlock
                    exclusive rewards based on business volume.
                  </p>
                  <p className="mb-1">
                    <span className="text-success fw-bold">✔</span> Earn cash
                    bonuses, luxury cars, branded vans, and international trips.
                  </p>
                  <p className="mb-0">
                    <span className="text-success fw-bold">✔</span> Rewards
                    range from thousands to multi-crore values based on
                    performance.
                  </p>
                </div>
              </div>

              {/* Highlighted Message */}
              <div className="col-md-5 d-flex align-items-center">
                <div className="text-center w-100 p-4 rounded-4 bg-white shadow-sm">
                  <p className="fs-6 mb-2">
                    Every aspect of our income structure is built to ensure that
                  </p>
                  <p className="fs-4 fw-bold text-success fst-italic mb-2">
                    your efforts are richly rewarded
                  </p>
                  <p className="fs-6 mb-0">
                    — creating a future where your{" "}
                    <span className="fw-bold text-success">
                      success is unstoppable
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= SUPPORT & GLOBAL EXPOSURE ================= */}
          <div className="p-4 mt-5 rounded" style={{ background: "#ffffff" }}>
            {/* HEADER */}
            <div className="text-center mb-4">
              <h3
                className="fw-bold text-white d-inline-block px-4 py-2 rounded underline"
                style={{ background: "#16a34a", fontSize: "32px" }}
              >
                Support & Global Exposure
              </h3>
            </div>

            <div className="row align-items-center g-5">
              {/* ================= LEFT IMAGE & LABELS ================= */}
              <div className="col-md-6 text-center">
                <p
                  className="fst-italic mt-3 mb-0"
                  style={{ color: "#15803d" }}
                >
                  From day one to global expansion <br />
                  <strong>– we support you at every step.</strong>
                </p>
                <img
                  src="/partnerSignup/8.3.png"
                  alt="Support & Global Exposure"
                  className="img-fluid mb-2"
                  style={{ maxHeight: "350px" }}
                />

                <h5 className="fw-bold text-success">
                  We’re With You Every Step
                </h5>
              </div>

              {/* ================= RIGHT CONTENT BOX ================= */}
              <div className="col-md-6">
                <div
                  className="p-4 rounded shadow-sm text-white"
                  style={{ background: "#0f3f3a" }}
                >
                  <ul className="list-unstyled mb-0">
                    <li className="mb-4 d-flex align-items-start gap-2">
                      <span className="fw-bold">•</span>
                      <div>
                        <strong>Fast Activation:</strong> Accounts are activated
                        within 30 minutes after verification.
                      </div>
                    </li>

                    <li className="mb-4 d-flex align-items-start gap-2">
                      <span className="fw-bold">•</span>
                      <div>
                        <strong>Comprehensive Training:</strong> Interactive,
                        hands-on sessions on product details, customer care, and
                        operations.
                      </div>
                    </li>

                    <li className="mb-4 d-flex align-items-start gap-2">
                      <span className="fw-bold">•</span>
                      <div>
                        <strong>Branding & Promotional Support:</strong> Full
                        access to promotional materials and expert marketing
                        guidance.
                      </div>
                    </li>

                    <li
                      className="d-flex align-items-start gap-2 p-2 rounded"
                      style={{
                        background: "#f0fdf4",
                        borderLeft: "4px solid #16a34a",
                      }}
                    >
                      <span className="fw-bold" style={{ color: "#16a34a" }}>
                        •
                      </span>
                      <div style={{ color: "#166534" }}>
                        <strong>Global Networking:</strong> Attend international
                        events (e.g., Singapore, Dubai) for global exposure.
                      </div>
                    </li>
                  </ul>
                </div>

                <p className="mt-3 text-success fw-semibold">
                  Our commitment is to support you at every step, helping you
                  grow your business and expand your reach.
                </p>
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= CHANNEL PARTNER COVERAGE ================= */}
          <div className="container my-5">
            <div
              className="w-full text-center mb-4"
              style={{ fontSize: "34px" }}
            >
              <span className="fw-bold me-3">Channel </span>
              <span
                className="fw-bold px-4 py-2 rounded-pill"
                style={{ background: "#12b347", color: "#ffffff" }}
              >
                Partner
              </span>
            </div>
            <div className="row align-items-center">
              {/* ================= LEFT CONTENT (9) ================= */}
              <div className="col-md-2">
                <ul className="list-unstyled fs-5">
                  <li className="mb-2 text-purple">
                    <span className="fw-bold">• Country</span>
                  </li>
                  <li className="mb-2 text-primary">
                    <span className="fw-bold">• State</span>
                  </li>
                  <li className="mb-2 text-info">
                    <span className="fw-bold">• Districts</span>
                  </li>
                  <li className="mb-2 text-success">
                    <span className="fw-bold">• Pin Code</span>
                  </li>
                  <li className="mb-2 text-warning">
                    <span className="fw-bold">• Retailer</span>
                  </li>
                </ul>
              </div>

              {/* ================= RIGHT IMAGES (3) ================= */}
              <div className="col-md-10">
                <div className="row align-items-center">
                  {/* Column 1 – 2 images */}
                  <div className="col-4 d-flex flex-column align-items-center gap-4">
                    <img
                      src="/partnerSignup/10.1.png"
                      alt="Location"
                      className="img-fluid"
                      style={{ maxWidth: "70px" }}
                    />

                    <img
                      src="/partnerSignup/10.2.png"
                      alt="Document"
                      className="img-fluid"
                      style={{ maxWidth: "70px" }}
                    />
                  </div>

                  {/* Column 2 – 1 BIG image */}
                  <div className="col-4 d-flex justify-content-center">
                    <img
                      src="/partnerSignup/10.3.png"
                      alt="World Map"
                      className="img-fluid"
                      style={{ maxWidth: "300px" }}
                    />
                  </div>

                  {/* Column 3 – 2 images */}
                  <div className="col-4 d-flex flex-column align-items-center gap-4">
                    <img
                      src="/partnerSignup/10.4.png"
                      alt="System"
                      className="img-fluid"
                      style={{ maxWidth: "80px" }}
                    />

                    <img
                      src="/partnerSignup/10.5.png"
                      alt="Revenue"
                      className="img-fluid"
                      style={{ maxWidth: "80px" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= CHANNEL PARTNER ================= */}
          <div className="p-4 mt-5 rounded" style={{ background: "#ffffff" }}>
            {/* Heading */}
            <div className="mb-3">
              <h2
                className="fw-bold mb-0 text-center"
                style={{ fontSize: "32px" }}
              >
                <span
                  className="px-3 py-1 rounded-pill me-2"
                  style={{
                    background: "#ffffff",
                    color: "#16a34a",
                    border: "2px solid #16a34a",
                  }}
                >
                  Channel
                </span>
                <span
                  className="px-4 py-1 rounded-pill text-white"
                  style={{ background: "#16a34a" }}
                >
                  Partner
                </span>
              </h2>
            </div>
            <div className="row align-items-center g-5">
              {/* ================= LEFT CONTENT ================= */}
              <div className="col-md-6">
                {/* Sub heading */}
                <h5 className="fst-italic text-success mb-3 text-xl">
                  Lead the Change in Healthcare Innovation
                </h5>

                {/* Description */}
                <p className="text-muted mb-3 text-lg">
                  Channel partners are dynamic business leaders ready to drive
                  change at regional and national levels.
                </p>

                {/* Emphasis Text */}
                <h5 className="fw-bold mb-2 text-xl">Build your legacy</h5>

                <p className="fw-semibold text-success mb-0 text-lg">
                  Transform Healthcare with Lifeonplus
                </p>
              </div>

              {/* ================= RIGHT IMAGE ================= */}
              <div className="col-md-6 text-center">
                <img
                  src="/partnerSignup/8.4.png"
                  alt="Channel Partner Model"
                  className="img-fluid"
                  style={{ maxHeight: "450px" }}
                />
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= INVEST SMART SECTION ================= */}
          <div className="container my-5">
            {/* Heading */}
            <div className="text-center mb-5">
              <h3
                className="fw-bold px-4 py-2 d-inline-block rounded-pill text-white"
                style={{ backgroundColor: "#0f4c55", fontSize: "32px" }}
              >
                Invest Smart, Earn Consistently
              </h3>
            </div>

            <div className="row align-items-center">
              {/* ================= LEFT: BIG IMAGE ================= */}
              <div className="col-md-8 text-center">
                <img
                  src="/partnerSignup/8.5.png" // <-- replace with actual image path
                  alt="Invest Smart Earn Consistently"
                  className="img-fluid"
                  style={{ maxHeight: "500px" }}
                />
              </div>

              {/* ================= RIGHT: TEXT CONTENT ================= */}
              <div className="col-md-4">
                <ul className="list-unstyled mb-4">
                  <li className="mb-3 fs-5 fw-semibold">✔ Direct Margins</li>
                  <li className="mb-3 fs-5 fw-semibold">
                    ✔ Corporate Incentives
                  </li>
                  <li className="mb-3 fs-5 fw-semibold">
                    ✔ Online Sales Incentives
                  </li>
                  <li className="mb-3 fs-5 fw-semibold">
                    ✔ Referral Commission
                  </li>
                  <li className="mb-3 fs-5 fw-semibold">
                    ✔ Passive Incentives
                  </li>
                </ul>

                {/* Highlight Box */}
                <div
                  className="p-4 rounded"
                  style={{ border: "2px solid #16a34a" }}
                >
                  <p className="fs-5 fw-bold mb-2">
                    Your investment today can lead to financial rewards tomorrow
                  </p>
                  <p className="fs-6 text-success fst-italic mb-0">
                    – earn money even while you are sleeping.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= COUNTRY PARTNER ================= */}
          <div className="container my-5">
            <div className="mb-3">
              <h2
                className="fw-bold mb-0 text-center"
                style={{ fontSize: "32px" }}
              >
                <span
                  className="fw-bold text-white px-4 py-2 rounded-pill me-3"
                  style={{ backgroundColor: "#0f4c55", fontSize: "32px" }}
                >
                  Country
                </span>
                <span
                  className="fw-bold text-white px-4 py-2 rounded-pill"
                  style={{ backgroundColor: "#16a34a", fontSize: "32px" }}
                >
                  Partner
                </span>
              </h2>
            </div>
            <div className="row align-items-center">
              {/* ================= LEFT CONTENT ================= */}
              <div className="col-md-6">
                {/* Bullet Content */}
                <ul className="fs-5">
                  <li className="mb-3">
                    <strong>Investment:</strong> ₹18 Cr (via MOU)
                  </li>

                  <li className="mb-3">
                    <strong>Setup:</strong> Minimum 1000 SQFT; 4 staff
                  </li>

                  <li className="mb-3">
                    <strong>Benefits:</strong> 5% direct margin, 3% corporate
                    and online incentives, 15% direct referral commission, and
                    5% passive income. Enjoy global meetings and exclusive
                    offers.
                  </li>

                  <li className="mb-3">
                    <strong>BPI:</strong> Fixed 2% per month for 12 months*
                  </li>

                  <li className="mb-3">
                    <strong>Projected Business Revenue:</strong> 1000+ Cr
                  </li>
                </ul>

                <p className="small text-muted mt-3">*T&C Apply.</p>
              </div>

              {/* ================= RIGHT IMAGE ================= */}
              <div className="col-md-6 text-center">
                <img
                  src="/partnerSignup/8.6.png" // <-- replace path
                  alt="Country Partner Benefits"
                  className="img-fluid"
                  style={{ maxHeight: "420px" }}
                />
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= COUNTRY PARTNER OPPORTUNITY ================= */}
          <div className="container my-5">
            {/* Heading */}
            <div className="text-center mb-5">
              <h2 className="fw-bold underline" style={{ fontSize: "34px" }}>
                Country Partner{" "}
                <span className="text-success">Opportunity</span>
              </h2>
            </div>

            <div className="row align-items-center">
              {/* ================= LEFT IMAGE (8 COL) ================= */}
              <div className="col-md-8 text-center mb-4 mb-md-0">
                <img
                  src="/partnerSignup/9.1.png" // replace path
                  alt="Country Partner Benefits"
                  className="img-fluid"
                  style={{ maxHeight: "420px" }}
                />
              </div>

              {/* ================= RIGHT IMAGE (4 COL) ================= */}
              <div className="col-md-4 text-center">
                <img
                  src="/partnerSignup/9.2.png" // replace path
                  alt="Country Partner Infrastructure"
                  className="img-fluid"
                  style={{ maxHeight: "380px" }}
                />
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= MARKET COVERAGE IN INDIA ================= */}
          <div className="container my-5">
            <div className="row align-items-center">
              {/* ================= LEFT CONTENT ================= */}
              <div className="col-md-7">
                <h2 className="fw-bold mb-4 text-4xl">
                  Market Coverage in <span className="text-success">India</span>
                </h2>

                <h4 className="text-success fw-bold mb-3 text-2xl">
                  Total States: 28
                </h4>

                <h5 className="fw-bold text-xl">Total Districts: 750+</h5>
                <p className="text-muted mb-4">
                  All Districts of a State will be coming under the State
                  Partner
                </p>

                <h5 className="fw-bold text-xl">Pin Code: 19000+</h5>
                <p className="text-muted">
                  All the Pin Code of a particular district will be coming under
                  respective Distributor
                </p>
              </div>

              {/* ================= RIGHT IMAGE ================= */}
              <div className="col-md-5 text-center">
                <img
                  src="/partnerSignup/9.3.png" // replace with your image path
                  alt="Market Coverage India"
                  className="img-fluid"
                  style={{ maxHeight: "480px" }}
                />
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= STATE PARTNER ================= */}
          <div className="container">
            <div className="text-center w-full">
              <h2
                className="fw-bold text-success mb-3 underline"
                style={{ fontSize: "34px" }}
              >
                State Partner
              </h2>
            </div>
            <div className="row align-items-center">
              {/* ================= LEFT CONTENT (4 COLS) ================= */}
              <div className="col-md-4">
                <ul className="list-unstyled text-dark">
                  <li className="mb-2">
                    <strong>Investment:</strong> ₹1.2 Cr
                  </li>

                  <li className="mb-2">
                    <strong>Setup:</strong> Minimum 400 SQFT; 2 staff
                  </li>

                  <li className="mb-2">
                    <strong>Benefits:</strong> 10% margin, 5% corporate/online
                    incentives, 15% referral commission, 5% passive income,
                    international networking (Singapore) & extra offers
                  </li>

                  <li className="mb-2">
                    <strong>BPI:</strong> Fixed 1.5% per month for 6 months*
                  </li>

                  <li className="mb-2">
                    <strong>Projected Business Revenue:</strong> 100+ Cr
                  </li>
                </ul>

                <small className="text-muted">*T&C Apply</small>
              </div>

              {/* ================= RIGHT IMAGES (8 COLS) ================= */}
              <div className="col-md-8">
                <div className="row align-items-center">
                  {/* Image 1 */}
                  <div className="col-md-6 text-center">
                    <img
                      src="/partnerSignup/11.1.png"
                      alt="Financial Benefits"
                      className="img-fluid"
                    />
                  </div>

                  {/* Image 2 */}
                  <div className="col-md-6 text-center">
                    <img
                      src="/partnerSignup/11.2.png"
                      alt="Earnings Flow"
                      className="img-fluid"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />
          {/* ================= DISTRIBUTOR ================= */}
          <div className="mb-5">
            <div className="text-center w-full">
              {" "}
              <h2
                className="fw-bold mb-4 text-success px-4 py-2 d-inline-block rounded underline"
                style={{ fontSize: "34px" }}
              >
                Distributor
              </h2>
            </div>
            <div className="row align-items-center">
              {/* LEFT CONTENT */}
              <div className="col-md-6">
                <ul className="list-unstyled mt-2">
                  <li className="mb-3">
                    <strong>Investment:</strong> <br />
                    <strong>Metro –</strong> ₹40 Lakh
                    <br />
                    <strong>Non-Metro – </strong> ₹5 Lakh
                  </li>

                  <li className="mb-3">
                    <strong>Setup:</strong> Minimum 200 SQFT; 1 staff
                  </li>

                  <li className="mb-3">
                    <strong>Benefits:</strong> 10% margin, 5% corporate/online
                    incentives, 15% referral, and 5% passive income with extra
                    incentives and 3 months support.
                  </li>

                  <li className="mb-3">
                    <strong>BPI:</strong> Fixed 1.5% per month for 6 months*
                  </li>

                  <li className="mb-3">
                    <strong>Projected Business Revenue:</strong> 10+ Cr
                  </li>

                  <li className="text-muted small">*T & C Apply</li>
                </ul>
              </div>

              {/* RIGHT IMAGE */}
              <div className="col-md-6 text-center mt-4 mt-md-0">
                <img
                  src="/partnerSignup/11.3.png" // your right-side image
                  alt="Distributor Flow Diagram"
                  className="img-fluid"
                  style={{ maxHeight: "420px" }}
                />
              </div>
            </div>
          </div>

          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= PIN CODE PARTNER ================= */}
          <div className="mb-5">
            <div className="w-full mb-4 text-center">
              {" "}
              <h2
                className="fw-bold px-4 text-success py-2 rounded underline me-3"
                style={{ fontSize: "34px" }}
              >
                Pin Code Partner
              </h2>
            </div>
            <div className="row align-items-center">
              {/* LEFT CONTENT */}
              <div className="col-md-6">
                {/* CONTENT LIST */}
                <ul className="list-unstyled">
                  <li className="mb-3">
                    <strong>Investment:</strong>
                    <br />
                    <strong>Metro – </strong>₹5L
                    <br />
                    <strong>Non-Metro – </strong>₹50K
                  </li>

                  <li className="mb-3">
                    <strong>Setup:</strong> 50 SQFT; 1 staff
                  </li>

                  <li className="mb-3">
                    <strong>Benefits:</strong> 15% margin, 10% corporate/online
                    incentives, 15% referral, and 5% passive income, with
                    support for local and international meetings (Singapore
                    trip).
                  </li>

                  <li className="mb-3">
                    <strong>BPI:</strong> Fixed 1.5% per month for 6 months*
                  </li>

                  <li className="mb-3">
                    <strong>Projected Business Revenue:</strong> Up to 90 Lakhs
                  </li>

                  <li className="text-muted small">*T & C Apply</li>
                </ul>
              </div>

              {/* RIGHT IMAGE */}
              <div className="col-md-6 text-center mt-4 mt-md-0">
                <img
                  src="/partnerSignup/11.4.png" // replace with your actual image path
                  alt="Pin Code Partner Program Structure"
                  className="img-fluid"
                  style={{ maxHeight: "420px" }}
                />
              </div>
            </div>
          </div>

          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />
          {/* ================= SALES IN PIN CODE ================= */}
          <div className="mb-5">
            {/* HEADING */}
            <div className="text-center mb-4">
              <h2
                className="fw-bold text-success underline"
                style={{ fontSize: "34px" }}
              >
                Sales in – Pin Code
              </h2>
              <p className="text-success fw-semibold mb-1 text-lg">
                Unlock Lucrative Opportunities in Your Pin Code
              </p>
              <p className="text-muted">
                Tap into a high-growth market and maximize your earning
                potential!
              </p>
            </div>

            <div className="row align-items-start">
              {/* LEFT LIST */}
              <div className="col-md-4">
                <ol className="ps-3 fw-semibold mx-4">
                  <li>1. Banks</li>
                  <li>2. Insurance</li>
                  <li>3. Associations</li>
                  <li>4. Corporates</li>
                  <li>5. Communities</li>
                  <li>6. Medical Fraternities</li>
                  <li>7. Institutions</li>
                  <li>8. Industries</li>
                  <li>9. Apartments</li>
                  <li>10. Show Rooms</li>
                  <li>11. Govt Organizations</li>
                  <li>12. Many More…</li>
                </ol>
              </div>

              {/* RIGHT IMAGES */}
              <div className="col-md-8">
                {/* ROW 1 → 4 IMAGES */}
                <div className="row mb-4 text-center">
                  <div className="col-md-3">
                    <img
                      src="/partnerSignup/12.1.png"
                      className="img-fluid"
                      alt="Banks"
                      style={{ height: "100px" }}
                    />
                  </div>
                  <div className="col-md-3">
                    <img
                      src="/partnerSignup/12.2.png"
                      className="img-fluid"
                      alt="Corporate"
                      style={{ height: "100px" }}
                    />
                  </div>
                  <div className="col-md-3">
                    <img
                      src="/partnerSignup/12.3.png"
                      className="img-fluid"
                      alt="Institution"
                      style={{ height: "100px" }}
                    />
                  </div>
                  <div className="col-md-3">
                    <img
                      src="/partnerSignup/12.4.png"
                      className="img-fluid"
                      alt="Industry"
                      style={{ height: "100px" }}
                    />
                  </div>
                </div>

                {/* ROW 2 → 3 IMAGES (CENTERED) */}
                <div className="row justify-content-center text-center">
                  <div className="col-md-3">
                    <img
                      src="/partnerSignup/12.5.png"
                      className="img-fluid"
                      alt="Hospital"
                      style={{ height: "100px" }}
                    />
                  </div>
                  <div className="col-md-3">
                    <img
                      src="/partnerSignup/12.6.png"
                      className="img-fluid"
                      alt="Community"
                      style={{ height: "100px" }}
                    />
                  </div>
                  <div className="col-md-3">
                    <img
                      src="/partnerSignup/12.7.png"
                      className="img-fluid"
                      alt="Business"
                      style={{ height: "100px" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= PROJECTED ANNUAL SALES – PIN CODE ================= */}
          <div className="mb-5">
            {/* TITLE */}
            <div className="text-center mb-4">
              <h2
                className="fw-bold text-success mb-2 underline"
                style={{ fontSize: "34px" }}
              >
                Projected Annual Sales <br />& Commission Structure for{" "}
                <span className="text-success">Pin Code</span>
              </h2>
              <p className="fw-semibold mb-0">Target – Pin Code 1 Year</p>
              <p className="text-muted">Average Product Cost ₹1,000</p>
            </div>

            <div className="row align-items-start">
              {/* LEFT SIDE – TABLE */}
              <div className="col-md-7 position-relative">
                {/* TABLE */}
                <div className="table-responsive">
                  <table className="table table-bordered text-center">
                    <thead className="table-primary">
                      <tr>
                        <th>Category</th>
                        <th>Units Sold (Metro)</th>
                        <th>Revenue (Metro)</th>
                        <th>Units Sold (Non-Metro)</th>
                        <th>Revenue (Non-Metro)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Individuals</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr>
                        <td>Corporates</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr>
                        <td>Institutions</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr>
                        <td>Industries</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr>
                        <td>Banks</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr>
                        <td>Insurance</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr>
                        <td>Associations</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr>
                        <td>Referral Marketing</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr>
                        <td>Online Sales</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr>
                        <td>Total Sales</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* 🔲 WHITE MASK – METRO */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "38%",
                    transform: "translate(-50%, -50%)",
                    background: "#fff",
                    padding: "30px 40px",
                    zIndex: 2,
                  }}
                />

                {/* 🔲 WHITE MASK – NON-METRO */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "82%",
                    transform: "translate(-50%, -50%)",
                    background: "#fff",
                    padding: "30px 40px",
                    zIndex: 2,
                  }}
                />

                {/* 💰 AMOUNTS */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "40%",
                    transform: "translate(-50%, -50%)",
                    fontSize: "32px",
                    fontWeight: "bold",
                    zIndex: 3,
                    pointerEvents: "none",
                  }}
                >
                  ₹90 Lakhs
                </div>

                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "75%",
                    transform: "translate(-50%, -50%)",
                    fontSize: "32px",
                    fontWeight: "bold",
                    zIndex: 3,
                    pointerEvents: "none",
                  }}
                >
                  ₹9 Lakhs
                </div>
              </div>

              {/* RIGHT SIDE – IMAGE */}
              <div className="col-md-5 text-center">
                <img
                  src="/partnerSignup/13.1.png"
                  alt="Commission Structure"
                  className="img-fluid"
                />
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= RETAILER SECTION ================= */}
          <div className="container mb-5">
            <div className="w-full text-center">
              {" "}
              <h2
                className="fw-bold mb-4 text-success underline"
                style={{
                  fontSize: "34px",
                }}
              >
                Retailer
              </h2>
            </div>
            <div className="row align-items-start">
              {/* LEFT SIDE – CONTENT */}
              <div className="col-md-5">
                <ul
                  className="mt-4"
                  style={{ fontSize: "16px", lineHeight: "1.8" }}
                >
                  <li className="mb-3">
                    <strong>Investment:</strong>
                    <br />
                    <strong>Metro – </strong>₹15k
                    <br />
                    <strong>Non-Metro – </strong>₹5K
                  </li>
                  <li className="mb-3">
                    <strong>Setup:</strong> No physical setup required.
                  </li>
                  <li className="mb-3">
                    <strong>Benefits:</strong> 25% margin, 15% referral
                    commission, a portion of 5% residual sales as continuous
                    bonus, with focused local branding support.
                  </li>
                  <li className="mb-3">
                    <strong>Projected Business Revenue:</strong> Multiple Lakhs
                  </li>
                  <li className="text-muted small">*T & C Apply</li>
                </ul>
              </div>

              {/* RIGHT SIDE */}
              <div className="col-md-7">
                {/* BIG IMAGE – FULL WIDTH */}
                <div className="mb-4 text-center">
                  <img
                    src="/partnerSignup/13.2.png"
                    alt="Retailer Benefits"
                    className="img-fluid w-100"
                    style={{ maxHeight: "260px", objectFit: "contain" }}
                  />
                </div>

                {/* BOTTOM PART – TEXT + SMALL IMAGE */}
                <div className="row align-items-center">
                  {/* TEXT CONTENT */}
                  <div className="col-md-7">
                    <p className="fst-italic small mt-3">
                      Every aspect of our income structure is built to ensure
                      that your efforts are richly rewarded — creating a future
                      where your success is unstoppable.
                    </p>
                  </div>

                  {/* SMALL IMAGE */}
                  <div className="col-md-5 text-center">
                    <img
                      src="/partnerSignup/13.3.png"
                      alt="Retailer Shop"
                      className="img-fluid"
                      style={{ height: "100px" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= CHANNEL PARTNER BENEFIT ================= */}
          <div className="container mb-5">
            {/* HEADING */}
            <h3
              className="fw-bold mb-4 text-center text-success underline"
              style={{ fontSize: "34px" }}
            >
              Channel Partner <span className="text-success">Benefit</span>
            </h3>

            {/* HIGHLIGHT STRIP */}
            <div
              className="mx-auto mb-4 px-4 py-3 text-white text-center rounded-pill"
              style={{ background: "#0aa84f", maxWidth: "700px" }}
            >
              <em>
                Every sale and referral brings you closer to a wealthier future.
              </em>
            </div>

            {/* LOGO + TEXT */}
            <div className="d-flex justify-content-center align-items-center mb-4">
              <img
                src="/partnerSignup/13.4.png"
                alt="Lifeonplus"
                style={{ width: "70px" }}
              />
              <div className="ms-3 fw-bold text-success fs-5">
                Earn as <br /> You Grow
              </div>
            </div>

            {/* INCOME & REWARDS STRUCTURE */}
            <div
              className="mx-auto p-4 text-white"
              style={{
                background: "#0b4b52",
                borderRadius: "18px",
                maxWidth: "850px",
              }}
            >
              <h5
                className="mb-4 px-4 py-2 rounded-pill text-center"
                style={{ background: "#0aa84f" }}
              >
                Income & Rewards Structure
              </h5>

              <ul style={{ lineHeight: "1.9", fontSize: "15px" }}>
                <li>
                  <strong>Direct Income:</strong> Fixed margin on every sale.
                </li>
                <li>
                  <strong>Referral Commissions:</strong> 15% commission for
                  every new partner introduced.
                </li>
                <li>
                  <strong>Passive Income:</strong> A portion of 5% residual
                  sales incentive as continuous bonus.
                </li>
                <li>
                  <strong>Reward Bonuses:</strong> Unlock additional rewards as
                  you grow – branded vans, luxury vehicles, and international
                  trips.
                </li>
              </ul>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= ABROAD TRIP ================= */}
          <div className="container my-5">
            {/* HEADER */}
            <div
              className="row align-items-center mb-4"
              style={{ textAlign: "center" }}
            >
              {/* LEFT IMAGE */}
              <div className="col-md-3 d-none d-md-block">
                <img
                  src="/partnerSignup/17.1.png" // replace with actual image
                  alt="Trip Left"
                  style={{ width: "100%", maxWidth: "180px" }}
                />
              </div>

              {/* CENTER CONTENT */}
              <div className="col-md-6">
                <h2
                  className="fw-bold text-success mb-1 underline"
                  style={{ fontSize: "40px" }}
                >
                  Abroad Trip
                </h2>

                <h5 className="fw-bold mb-1" style={{ fontSize: "20px" }}>
                  Pre Launch Offer for Members!
                </h5>

                <p className="text-primary fw-semibold mb-1">
                  Bangkok | Singapore | Dubai
                </p>

                <h6 className="fw-bold">Qualifying Criteria for the Trip</h6>
              </div>

              {/* RIGHT IMAGE */}
              <div className="col-md-3 d-none d-md-block">
                <img
                  src="/partnerSignup/17.2.png" // replace with actual image
                  alt="Trip Right"
                  style={{ width: "100%", maxWidth: "180px" }}
                />
              </div>
            </div>

            <div className="row align-items-center">
              {/* LEFT SIDE – SINGLE IMAGE */}
              <div className="col-md-4 text-center mb-4 mb-md-0">
                <img
                  src="/partnerSignup/17.png"
                  alt="Member Rewards"
                  className="img-fluid"
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </div>

              {/* RIGHT SIDE – TEXT ONLY */}
              <div className="col-md-8">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "18px",
                  }}
                >
                  {/* ITEM */}
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ fontWeight: "600", color: "#28a745" }}>
                      Non-Metro Pin Code Partner – 20 Referrals
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "14px",
                        marginTop: "4px",
                      }}
                    >
                      <span>Trip + 15% Referral Incentive + Instant Gift</span>
                      <span style={{ fontWeight: "600" }}>
                        1% Passive Income
                      </span>
                    </div>
                  </div>

                  {/* ITEM */}
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ fontWeight: "600", color: "#0d6efd" }}>
                      Metro Pin Code Partner – 2 Referrals
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "14px",
                        marginTop: "4px",
                      }}
                    >
                      <span>Trip + 15% Referral Incentive + Instant Gift</span>
                      <span style={{ fontWeight: "600" }}>
                        1% Passive Income
                      </span>
                    </div>
                  </div>

                  {/* ITEM */}
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ fontWeight: "600", color: "#8bc34a" }}>
                      Non-Metro Distributor – 1 Referral
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "14px",
                        marginTop: "4px",
                      }}
                    >
                      <span>Trip + 15% Referral Incentive + Instant Gift</span>
                      <span style={{ fontWeight: "600" }}>
                        1.5% Passive Income
                      </span>
                    </div>
                  </div>

                  {/* ITEM */}
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ fontWeight: "600", color: "#ffc107" }}>
                      Metro Distributor – 1 Referral
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "14px",
                        marginTop: "4px",
                      }}
                    >
                      <span>
                        Trip for 2 Persons + 15% Referral Incentive + Instant
                        Gift
                      </span>
                      <span style={{ fontWeight: "600" }}>
                        1.5% Passive Income
                      </span>
                    </div>
                  </div>

                  {/* ITEM */}
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ fontWeight: "600", color: "#fd7e14" }}>
                      Country Partner – 1 Referral
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "14px",
                        marginTop: "4px",
                      }}
                    >
                      <span>
                        Trip for 3 Persons + 15% Referral Incentive + Instant
                        Gift
                      </span>
                      <span style={{ fontWeight: "600" }}>
                        3% Passive Income
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= YOUR JOURNEY STARTS HERE ================= */}
          <div className="container my-5">
            {/* TITLE */}
            <div className="w-full mb-4 text-center">
              <h2
                className="fw-bold mb-0 text-success text-center underline"
                style={{ fontSize: "34px" }}
              >
                Your Journey Starts Here
              </h2>
            </div>

            <div className="row align-items-start">
              {/* LEFT SIDE – TICK IMAGE */}
              <div className="col-md-4 text-center mb-4 mb-md-0">
                <img
                  src="/partnerSignup/15.1.png"
                  alt="Verified"
                  className="img-fluid"
                  style={{ maxWidth: "280px" }}
                />
              </div>

              {/* RIGHT SIDE – CONTENT */}
              <div className="col-md-8">
                <ul className="list-unstyled">
                  <li className="mb-3">
                    <strong>
                      Submit your LOI & Complete KYC with Payment:
                    </strong>{" "}
                    Begin your partnership journey with a simple application.
                  </li>

                  <li className="mb-3">
                    <strong>Fast Activation:</strong> Get activated within 30
                    minutes.
                  </li>

                  <li className="mb-3">
                    <strong>Comprehensive Training:</strong> Gain the skills and
                    knowledge needed to succeed.
                  </li>

                  <li className="mb-3">
                    <strong>Launch Your Network:</strong> Start expanding your
                    network and generating income.
                  </li>

                  <li className="mb-4">
                    <strong>Global Engagement:</strong> Attend international
                    events and unlock exclusive rewards.
                  </li>
                </ul>

                {/* BOTTOM RIGHT SECTION */}
                <div className="row align-items-center mt-4">
                  {/* TEXT */}
                  <div className="col-md-6 mb-3 mb-md-0">
                    <p className="fw-bold mb-1">
                      Your investment today can lead to financial rewards
                      tomorrow—
                    </p>
                    <p
                      className="text-success fst-italic fw-semibold mb-0"
                      style={{ fontSize: "18px" }}
                    >
                      earn money even while you are sleeping.
                    </p>
                    <div
                      style={{
                        width: "120px",
                        height: "2px",
                        background: "#000",
                        marginTop: "8px",
                      }}
                    ></div>
                  </div>

                  {/* EARTH IMAGE */}
                  <div className="col-md-6 text-center">
                    <img
                      src="/partnerSignup/15.2.png"
                      alt="Global Network"
                      className="img-fluid"
                      style={{ width: "150px" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />
          {/* ================= Trip to Singapore ================= */}
          <div className="container my-5">
            {/* TITLE */}
            <h2
              className="fw-bold text-center mb-4 underline"
              style={{ fontSize: "34px" }}
            >
              ONCE YOU JOIN AS A{" "}
              <span className="text-success">LIFEONPLUS</span> PARTNER, YOU GET
            </h2>

            <div className="d-flex justify-content-center align-items-center gap-3 mb-5">
              <span
                className="badge bg-success fs-4 px-4 py-2 rounded-pill d-flex align-items-center"
                style={{ height: "60px" }}
              >
                Trip to Singapore
              </span>

              <img
                src="/partnerSignup/16.10.png"
                alt="Singapore"
                className="img-fluid"
                style={{ width: "70px", height: "70px", objectFit: "contain" }}
              />
            </div>

            {/* MAIN ROW */}
            <div className="row align-items-center">
              {/* LEFT – GIFT IMAGE */}
              <div className="col-md-4 text-center mb-4 mb-md-0">
                <img
                  src="/partnerSignup/16.1.png"
                  alt="Welcome Gift"
                  className="img-fluid"
                  style={{ maxWidth: "280px" }}
                />
              </div>

              {/* RIGHT – CONTENT */}
              <div className="col-md-8">
                <p className="mb-4">
                  <strong>Welcome Perks 🎁</strong> – Receive exclusive
                  onboarding benefits. A premium onboarding package to kickstart
                  your journey.
                </p>

                {/* ICON GRID */}
                <div className="row text-center g-3 mb-4">
                  {[
                    "16.2.png",
                    "16.3.png",
                    "16.4.png",
                    "16.5.png",
                    "16.6.png",
                    "16.7.png",
                    "16.8.png",
                    "16.9.png",
                  ].map((img, index) => (
                    <div className="col-3" key={index}>
                      <img
                        src={`/partnerSignup/${img}`}
                        alt=""
                        className="img-fluid"
                        style={{ maxHeight: "70px" }}
                      />
                    </div>
                  ))}
                </div>

                {/* BENEFITS LIST */}
                <ul className="list-unstyled">
                  <li className="mb-2">
                    ✅ <strong>Official Partner Certificate</strong> – Trusted
                    Lifeonplus Partner
                  </li>
                  <li className="mb-2">
                    ✅ <strong>Partner Business Kit</strong> – Tools &
                    guidelines
                  </li>
                  <li className="mb-2">
                    ✅ <strong>Marketing Materials</strong> – Posters, creatives
                    & branding
                  </li>
                  <li className="mb-2">
                    ✅ <strong>Sightseeing & Induction</strong> – Networking &
                    attractions
                  </li>
                  <li className="mb-2">
                    ✅ <strong>Additional Incentives</strong> – Commission &
                    bonuses
                  </li>
                  <li>
                    ✅ <strong>Shared Business Opportunities</strong> – More
                    revenue streams
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <hr
            style={{
              height: "4px",
              backgroundColor: "#1DB954",
              border: "none",
              marginBottom: "50px",
            }}
          />

          {/* ================= CHANNEL PARTNER T&C ================= */}
          <div className="container mb-5">
            {/* TITLE */}
            <h3
              className="fw-bold text-center mb-4 underline"
              style={{ fontSize: "34px" }}
            >
              <span className="text-success">Channel Partner</span> Terms and
              Conditions (T&amp;C)
            </h3>

            <div className="row align-items-center">
              {/* LEFT IMAGES */}
              <div className="col-md-2 text-center">
                <img
                  src="/partnerSignup/14.1.png"
                  alt="T&C"
                  className="img-fluid mb-4"
                  style={{ maxWidth: "120px" }}
                />
                <img
                  src="/partnerSignup/14.2.png"
                  alt="KYC"
                  className="img-fluid"
                  style={{ maxWidth: "120px" }}
                />
              </div>

              {/* CENTER TABLE */}
              <div className="col-md-8">
                <table className="table table-bordered text-sm">
                  <thead>
                    <tr>
                      <th
                        colSpan="2"
                        className="text-center"
                        style={{ backgroundColor: "#0aa84f", color: "#ffffff" }}
                      >
                        Terms &amp; Conditions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td
                        className="fw-bold text-white"
                        style={{ background: "#0aa84f" }}
                      >
                        KYC
                      </td>
                      <td>
                        All channel partners have to submit 100% Payment with
                        KYC – after LOI
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-bold text-white"
                        style={{ background: "#0aa84f" }}
                      >
                        Activation
                      </td>
                      <td>
                        Company will activate respective partner's account
                        within 30 minutes to 2 hours (working days: Monday to
                        Friday).
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-bold text-white"
                        style={{ background: "#0aa84f" }}
                      >
                        Onboarding Training
                      </td>
                      <td>
                        Company provides eligible partners offers during the
                        seminar within 10 working days from the payment receipt.
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-bold text-white"
                        style={{ background: "#0aa84f" }}
                      >
                        International Trip
                      </td>
                      <td>
                        Meetings for eligible domestic and international
                        partners will take place quarterly (as and when
                        scheduled) with minimum group of 20 people.
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-bold text-white"
                        style={{ background: "#0aa84f" }}
                      >
                        Partner Kit
                      </td>
                      <td>
                        All promotional materials will be provided within 10
                        working days from account activation, delivered directly
                        by local partner or nearest company representative
                        office.
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-bold text-white"
                        style={{ background: "#0aa84f" }}
                      >
                        Direct Incentives
                      </td>
                      <td>
                        If the partners do not have a registered company,
                        incentives will be given directly to respective
                        partners.
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-bold text-white"
                        style={{ background: "#0aa84f" }}
                      >
                        GST Billing
                      </td>
                      <td>
                        For partners with a registered company and GST,
                        invoicing will be done to the respective partner’s
                        company.
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-bold text-white"
                        style={{ background: "#0aa84f" }}
                      >
                        Incentives
                      </td>
                      <td>Distributed on a weekly basis.</td>
                    </tr>

                    <tr>
                      <td
                        className="fw-bold text-white"
                        style={{ background: "#0aa84f" }}
                      >
                        Deductions
                      </td>
                      <td>
                        All direct partner referrals and incentives are subject
                        to deduction of 18% GST, 5% Service Charge and TDS as
                        applicable (other deductions if any).
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-bold text-white"
                        style={{ background: "#0aa84f" }}
                      >
                        Training
                      </td>
                      <td>
                        Product/Concept Training, Ecosystem Briefing, Partner
                        Training, Customer Service/Care, Application Training,
                        and more.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* RIGHT IMAGES */}
              <div className="col-md-2 text-center">
                <img
                  src="/partnerSignup/14.3.png"
                  alt="Time"
                  className="img-fluid mb-4"
                  style={{ maxWidth: "120px" }}
                />
                <img
                  src="/partnerSignup/14.4.png"
                  alt="Agreement"
                  className="img-fluid"
                  style={{ maxWidth: "120px" }}
                />
              </div>
            </div>
          </div>

          <div className="text-center py-5 bg-white border-top">
            <h4
              className="fw-bold text-success mb-5 text-center"
              style={{
                fontSize: "32px",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Invest in a Healthier Future – Join Lifeonplus Today!
            </h4>

            <button
              variant="success"
              size="lg"
              className="px-5 py-3 fw-bold"
              onClick={proceedToAction}
            >
              Proceed to Register
            </button>

            <p className="mt-4 text-muted">
              Fast Activation • Comprehensive Training • Global Networking •
              Lucrative Rewards
            </p>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            variant="outline-secondary"
            onClick={() => setShowDetailsModal(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
      <RoleInfoModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        role={selectedType}
      />

      <style>
        {`
          .modal-90w {
            width: 90%;
            max-width: 800px;
            margin: auto;
          }
          .modal-80w {
            width: 80%;
            max-width: 500px;
            margin: auto;
          }
          @media (min-width: 768px) {
            .modal-md {
              max-width: 400px;
            }
            .modal-lg {
              max-width: 700px;
            }
          }
          .modern-input {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 12px 16px;
            background-color: #fff;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            transition: all 0.2s ease;
            outline: none;
          }
          .modern-input:hover {
            border-color: #a3a3a3;
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
          }
          .modern-input:focus {
            border-color: #35A93E;
            box-shadow: 0 0 0 3px rgba(53, 169, 62, 0.2);
          }
          .modern-input:disabled, .modern-input[readonly] {
            background-color: #f3f4f6;
            cursor: not-allowed;
            box-shadow: none;
          }
          @media (max-width: 767px) {
            header {
              margin-left: 0;
              margin-right: 0;
            }
            .mx-5 {
              margin-left: 0;
              margin-right: 0;
            }
            .absolute.top-full {
              width: 100%;
              max-height: 60vh;
            }
            .absolute.top-full img {
              width: 8vw;
              height: 8vw;
            }
            .absolute.top-full p {
              font-size: 3vw;
            }
              
          }

        `}
      </style>
    </>
  );
};

export default Header;
